import React, { useState, useEffect, useRef } from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  Link,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemIcon,
  Divider,
  Select,
  FormControl,
  InputLabel,
  DialogContentText,
  TextField,
  Slider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  CircularProgress,
  ListSubheader
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import ImageIcon from '@mui/icons-material/Image';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import { Save, GavelOutlined, OpenInNew } from '@mui/icons-material';
import ElementStyleEditorModal from './ElementStyleEditorModal';
import ImageLibrarySelectionModal from '../../accounting/inventory/ImageLibrarySelectionModal'; // ✅ YENİ: Resim kütüphanesi modalı
import { ImageUploadService } from '../../accounting/inventory/services/imageUploadService';
import { getAboutTabs, getContactSettings, getCustomPages, getFooterSettings, getLegalDocuments, saveFooterSettings as saveFooterSettingsAPI } from '../../../services/ecommerceApi';
import { useTranslation } from 'react-i18next';
import { notifyEcommerceSaved } from '../utils/ecommerceSaveToast';
import GuideButton from '../../common/guide/GuideButton';
import type { EcommerceTabId } from '../navigationConfig';
import {
  buildPageLinkUrl,
  buildTabLocations,
  FOOTER_PAGE_OPTIONS,
  formatFooterLinkSecondary,
  getStaticLocationsForPage,
  resolveLocationValue,
  TAB_TARGET_CONFIGS,
  type FooterLocationOption,
  type FooterPageOption,
  type FooterQuickLink,
} from '../utils/footerQuickLinks';
import {
  addQuickAccessSlot,
  getFooterColumnKey,
  moveFooterColumnInSide,
  removeQuickAccessSlot,
  resolveFooterAlign,
  resolveFooterColumnOrder,
  resolveFooterColumnSide,
  setFooterColumnSide,
  splitFooterColumnsBySide,
  FOOTER_COLUMN_CONTENT_ALIGN,
  type FooterAlign,
  type FooterColumnSide,
  type FooterColumnSlot,
  type FooterIconLayout,
} from '../utils/footerColumns';
import {
  PanelSocialIcon,
  SOCIAL_PLATFORM_OPTIONS,
  getSocialPlatformLabel,
  type SocialPlatform,
} from '../utils/footerSocialIcons';
import { normalizeFooterLayout, type FooterLayoutId } from '../utils/footerTemplates';
import FooterTemplatePicker from './FooterTemplatePicker';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

// QuickAccessColumn interface
interface QuickAccessColumn {
  id: number;
  title?: string;
  maxLinks: number;
  hasFixedLinks?: boolean;
}

// QuickLink interface
type QuickLink = FooterQuickLink;

const MAX_QUICK_ACCESS_COLUMNS = 4;

const DEFAULT_QUICK_ACCESS_COLUMNS: QuickAccessColumn[] = [
  { id: 1, maxLinks: 7, hasFixedLinks: true },
  { id: 2, maxLinks: 10, hasFixedLinks: false },
];

const hasSavedQuickAccessColumnConfig = (
  columns: QuickAccessColumn[] | null | undefined,
  elementStyleColumns: QuickAccessColumn[] | null | undefined
): boolean => (columns?.length ?? 0) > 0 || (elementStyleColumns?.length ?? 0) > 0;

const hasCustomColumnTitles = (columns: QuickAccessColumn[]): boolean =>
  columns.some((column) => Boolean(column.title?.trim()));

const shouldPersistQuickAccessColumns = (columns: QuickAccessColumn[]): boolean =>
  Array.isArray(columns);

const buildQuickAccessColumn = (
  id: number,
  meta?: Partial<QuickAccessColumn> | null
): QuickAccessColumn => {
  const defaults =
    id === 1
      ? { id, maxLinks: 7, hasFixedLinks: true }
      : { id, maxLinks: 10, hasFixedLinks: false };
  const title = meta?.title?.trim();

  return {
    ...defaults,
    ...meta,
    id,
    title: title || undefined,
    maxLinks: meta?.maxLinks ?? defaults.maxLinks,
    hasFixedLinks: meta?.hasFixedLinks ?? defaults.hasFixedLinks,
  };
};

const resolveQuickAccessColumns = (
  columns: QuickAccessColumn[] | null | undefined,
  elementStyleColumns: QuickAccessColumn[] | null | undefined,
  quickLinks: QuickLink[] = []
): QuickAccessColumn[] => {
  const fromJson = columns ?? [];
  const fromStyles = elementStyleColumns ?? [];
  const hasConfig = hasSavedQuickAccessColumnConfig(columns, elementStyleColumns);

  if (!hasConfig) {
    return normalizeQuickAccessColumns(undefined, quickLinks);
  }

  const metadata = fromStyles.length > 0 ? fromStyles : fromJson;
  return normalizeQuickAccessColumns(metadata, quickLinks);
};

const getQuickAccessColumnHeader = (
  column: QuickAccessColumn,
  columnIndex: number,
  columns: QuickAccessColumn[],
  sectionTitle: string
): string | null => {
  if (hasCustomColumnTitles(columns)) {
    return column.title?.trim() || null;
  }

  if (columnIndex === 0) {
    return sectionTitle.trim() || 'Hızlı Erişim';
  }

  return null;
};

const normalizeQuickAccessColumns = (
  columns: QuickAccessColumn[] | null | undefined,
  quickLinks: QuickLink[] = []
): QuickAccessColumn[] => {
  const linkColumnIds = [...new Set(quickLinks.map((link) => link.column || 1))];

  if (!columns || columns.length === 0) {
    const requiredCount = Math.max(2, ...linkColumnIds, 0);
    return Array.from({ length: requiredCount }, (_, index) => buildQuickAccessColumn(index + 1));
  }

  const byId = new Map<number, QuickAccessColumn>();
  for (const column of columns) {
    if (!column || !Number.isFinite(column.id)) continue;
    byId.set(column.id, buildQuickAccessColumn(column.id, column));
  }
  for (const id of linkColumnIds) {
    if (!byId.has(id)) {
      byId.set(id, buildQuickAccessColumn(id));
    }
  }

  return [...byId.values()].sort((a, b) => a.id - b.id);
};

const getQuickAccessGridTemplate = (columnCount: number) => ({
  xs: '1fr',
  sm: columnCount >= 2 ? 'repeat(2, 1fr)' : '1fr',
  md: columnCount >= 3 ? 'repeat(3, 1fr)' : columnCount >= 2 ? 'repeat(2, 1fr)' : '1fr',
  lg: `repeat(${columnCount}, 1fr)`,
});

// SocialLink interface
interface SocialLink {
  id: string;
  platform: SocialPlatform;
  icon: string;
  url: string;
}

// Styled components
const SectionTitle = React.memo(({ children, ...props }: any) => (
  <Typography 
    variant="h6" 
    sx={{ 
      fontWeight: 'bold',
      marginBottom: 2,
      marginTop: 3,
      display: 'flex',
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        marginRight: 1,
      }
    }}
    {...props}
  >
    {children}
  </Typography>
));

// Footer Settings Props
interface FooterSettingsProps {
  settings: {
    showSocialLinks: boolean;
    showPaymentMethods: boolean;
    showNewsletter: boolean;
    backgroundColor: string;
    textColor: string;
    itemsPerRow: number;
    showRatings: boolean;
    enableQuickView: boolean;
    enableWishlist: boolean;
  };
  onSettingsChange: (setting: keyof FooterSettingsProps['settings'], value: any) => void;
  companyId: string | null;
  configId: string; // ✅ YENİ: SQL için configId
  onNavigate?: (tab: EcommerceTabId) => void;
}

const FooterSettings: React.FC<FooterSettingsProps> = ({
  settings,
  onSettingsChange,
  companyId,
  configId, // ✅ YENİ
  onNavigate
}) => {
  const { t } = useTranslation('salesSystem');
  const currentYear = new Date().getFullYear();
  
  // Örnek şirket bilgileri
  const exampleCompany = {
    name: t('ecommerceFooterSettings.preview.companyName'),
    description: t('ecommerceFooterSettings.preview.companyDescription'),
    address: t('ecommerceFooterSettings.preview.companyAddress'),
    phone: '+90 (212) 123 45 67',
    email: 'info@ornek-sirket.com'
  };

  // Element stilleri için state
  const [elementStyles, setElementStyles] = useState({
    companyName: { fontSize: '1.25rem', fontWeight: 'bold', color: '#ffffff', fontStyle: 'normal', fontFamily: 'Arial' },
    companyDescription: { 
      fontSize: '0.875rem', 
      fontWeight: 400, 
      color: 'rgba(255, 255, 255, 0.8)', 
      fontStyle: 'normal', 
      fontFamily: 'Arial',
      text: t('ecommerceFooterSettings.preview.companyDescription')
    },
    sectionTitle: { fontSize: '1.25rem', fontWeight: 'bold', color: '#ffffff', fontStyle: 'normal', fontFamily: 'Arial' },
    contactInfo: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    quickLinks: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    copyright: { fontSize: '0.875rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontStyle: 'normal', fontFamily: 'Arial' },
    backgroundColor: { backgroundColor: '#1f2937' },
    newsletter: {
      buttonText: t('ecommerceFooterSettings.newsletter.subscribe'),
      showGender: true,
      image: '',
      imageWidth: 400,
      imageHeight: 350,
      bgColor: '#ffffff',
      imageSpacing: 0,
      mobileImage: '',
      mobileImageWidth: 280,
      mobileImageHeight: 200,
      showImageOnMobile: true,
    } as Record<string, any>
  });

  // Footer ayarları için state
  const [footerSettings, setFooterSettings] = useState({
    backgroundColor: '#1f2937',
    textColor: '#ffffff',
    textOpacity: 0.8,
    companyName: '',
    copyrightText: '',
    contactTitle: '',
    quickLinksTitle: '',
    // Yasal belgeler kolonu (otomatik)
    showLegalColumn: true,
    legalColumnTitle: 'Yasal',
    socialLinks: [] as SocialLink[], // Sosyal medya linkleri için state
    quickLinks: [] as QuickLink[], // Hızlı erişim linkleri için state
    quickAccessColumns: DEFAULT_QUICK_ACCESS_COLUMNS as QuickAccessColumn[],
    socialIconSize: 24, // İkon boyutu için state
    iconLayout: 'row' as FooterIconLayout,
    columnOrder: [] as FooterColumnSlot[],
    showContactIcons: true,
    enableFooterAnimation: true,
    footerAlign: 'left' as FooterAlign,
    companyLogo: '', // Şirket logosu URL'i
    logoWidth: 150, // Logo genişliği
    logoHeight: 60, // Logo yüksekliği
    companyLogo2: '', // İkinci şirket logosu URL'i
    logo2Width: 150, // İkinci logo genişliği
    logo2Height: 60, // İkinci logo yüksekliği
    // İletişim Bilgileri
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    // Newsletter / Bülten Bölümü
    showNewsletterSection: false,
    newsletterTitle: '',
    newsletterDescription: '',
    newsletterButtonText: t('ecommerceFooterSettings.newsletter.subscribe'),
    showGenderSelection: true,
    newsletterImage: '',
    newsletterImageWidth: 400,
    newsletterImageHeight: 350,
    newsletterBgColor: '#ffffff',
    newsletterImageSpacing: 0,
    newsletterMobileImage: '',
    newsletterMobileImageWidth: 280,
    newsletterMobileImageHeight: 200,
    newsletterShowImageOnMobile: true,
    // Footer alt şerit: Visa / Mastercard vb. ödeme logoları
    paymentLogosImages: [] as string[],
    paymentLogosWidth: 160,
    paymentLogosHeight: 40,
    paymentLogosAlign: 'left' as 'left' | 'center' | 'right',
    footerLayout: 'layout1' as FooterLayoutId,
    legalEntityName: '',
  });

  type FooterImageSlot = 'logo1' | 'logo2' | 'newsletter' | 'newsletterMobile' | 'paymentLogos';

  // ✅ YENİ: Resim kütüphanesi modalı için state
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [currentLogoType, setCurrentLogoType] = useState<FooterImageSlot>('logo1');
  const [paymentDropActive, setPaymentDropActive] = useState(false);
  const [paymentUploading, setPaymentUploading] = useState(false);
  const paymentFileInputRef = useRef<HTMLInputElement | null>(null);

  // Sağ tık menüsü için state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    target: keyof typeof elementStyles;
  } | null>(null);

  // Stil düzenleme modalı için state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingElementKey, setEditingElementKey] = useState<keyof typeof elementStyles | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Arka plan rengi düzenleme modalı için state
  const [isBackgroundModalOpen, setIsBackgroundModalOpen] = useState(false);
  const [tempBackgroundColor, setTempBackgroundColor] = useState('#1f2937');

  // Şirket açıklaması için özel modal state
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [tempDescription, setTempDescription] = useState(t('ecommerceFooterSettings.preview.companyDescription'));
  const [tempDescriptionStyles, setTempDescriptionStyles] = useState({
    fontSize: '0.875rem', 
    fontWeight: 400, 
    color: 'rgba(255, 255, 255, 0.8)', 
    fontStyle: 'normal', 
    fontFamily: 'Arial'
  });

  const [legalPreviewDocs, setLegalPreviewDocs] = useState<Array<{ title: string }>>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Sosyal medya modalı için state
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [editingSocialLink, setEditingSocialLink] = useState<SocialLink | null>(null);

  // Sosyal medya silme onayı için state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingSocialLinkId, setDeletingSocialLinkId] = useState<string | null>(null);

  // Hızlı Erişim modalı için state
  const [isQuickLinkModalOpen, setIsQuickLinkModalOpen] = useState(false);
  const [editingQuickLink, setEditingQuickLink] = useState<QuickLink | null>(null);
  const [quickLinkDefaultColumn, setQuickLinkDefaultColumn] = useState<number | undefined>(undefined);

  // Hızlı Erişim silme onayı için state
  const [isQuickLinkDeleteConfirmOpen, setIsQuickLinkDeleteConfirmOpen] = useState(false);
  const [deletingQuickLinkId, setDeletingQuickLinkId] = useState<string | null>(null);

  // Hızlı Erişim sütun silme onayı için state
  const [isColumnDeleteConfirmOpen, setIsColumnDeleteConfirmOpen] = useState(false);
  const [deletingColumnId, setDeletingColumnId] = useState<number | null>(null);
  
  // SQL Footer Settings ID
  const [footerSettingsId, setFooterSettingsId] = useState<string | null>(null);
  
  // ✅ AUTO-SAVE: Debounce timer
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const footerSettingsRef = useRef(footerSettings);
  const elementStylesRef = useRef(elementStyles);

  useEffect(() => {
    footerSettingsRef.current = footerSettings;
  }, [footerSettings]);

  useEffect(() => {
    elementStylesRef.current = elementStyles;
  }, [elementStyles]);
  
  // ✅ AUTO-SAVE: İlk yükleme kontrolü
  const isInitialLoadRef = useRef(true);

  const [contactPreview, setContactPreview] = useState({
    email: '',
    phone: '',
    address: '',
    workingHours: '',
  });

  // ✅ SQL'den ayarları yükle
  useEffect(() => {
    const loadFooterSettingsFromSQL = async () => {
      if (!configId || !companyId) { // ✅ companyId null check
        console.log('⚠️ [FOOTER SETTINGS] ConfigId veya CompanyId yok, yükleme atlanıyor');
        return;
      }

      console.log('🔄 [FOOTER SETTINGS] SQL den ayarlar yükleniyor...', { configId });

      try {
        const response = await getFooterSettings(companyId, configId); // ✅ companyId eklendi
        
        if (response.success && response.data) {
          console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla yüklendi:', response.data);
          setFooterSettingsId(response.data.id);
          
          // Element stillerini parse et
          let elementStylesData = elementStyles;
          if (response.data.elementStylesJson) {
            try {
              elementStylesData = JSON.parse(response.data.elementStylesJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] ElementStylesJson parse hatası:', e);
            }
          }
          
          // Sosyal medya linklerini parse et
          let socialLinksData: SocialLink[] = [];
          if (response.data.socialLinksJson) {
            try {
              socialLinksData = JSON.parse(response.data.socialLinksJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] SocialLinksJson parse hatası:', e);
            }
          }
          
          // Hızlı erişim linklerini parse et
          let quickLinksData: QuickLink[] = [];
          if (response.data.quickLinksJson) {
            try {
              quickLinksData = JSON.parse(response.data.quickLinksJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] QuickLinksJson parse hatası:', e);
            }
          }

          // Hızlı erişim sütunlarını parse et
          let quickAccessColumnsFromJson: QuickAccessColumn[] = [];
          if (response.data.quickAccessColumnsJson) {
            try {
              quickAccessColumnsFromJson = JSON.parse(response.data.quickAccessColumnsJson);
            } catch (e) {
              console.error('❌ [FOOTER SETTINGS] QuickAccessColumnsJson parse hatası:', e);
            }
          }

          const quickAccessColumnsFromStyles = Array.isArray((elementStylesData as Record<string, unknown>)?.quickAccessColumns)
            ? ((elementStylesData as Record<string, unknown>).quickAccessColumns as QuickAccessColumn[])
            : [];

          const quickAccessColumnsData = resolveQuickAccessColumns(
            quickAccessColumnsFromJson,
            quickAccessColumnsFromStyles,
            quickLinksData
          );

          const persistedElementStyles = { ...elementStylesData } as typeof elementStyles;
          if (shouldPersistQuickAccessColumns(quickAccessColumnsData)) {
            (persistedElementStyles as Record<string, unknown>).quickAccessColumns = quickAccessColumnsData;
          } else {
            delete (persistedElementStyles as Record<string, unknown>).quickAccessColumns;
          }

          const columnOrderData = resolveFooterColumnOrder(
            (elementStylesData as Record<string, unknown>)?.columnOrder as FooterColumnSlot[] | undefined,
            quickAccessColumnsData,
            { includeLegal: response.data.showLegalColumn !== false }
          );
          (persistedElementStyles as Record<string, unknown>).columnOrder = columnOrderData;
          (persistedElementStyles as Record<string, unknown>).iconLayout =
            (elementStylesData as Record<string, unknown>)?.iconLayout === 'grid' ? 'grid' : 'row';
          (persistedElementStyles as Record<string, unknown>).showContactIcons =
            (elementStylesData as Record<string, unknown>)?.showContactIcons !== false;
          (persistedElementStyles as Record<string, unknown>).enableFooterAnimation =
            (elementStylesData as Record<string, unknown>)?.enableFooterAnimation !== false;
          (persistedElementStyles as Record<string, unknown>).footerAlign = resolveFooterAlign(
            (elementStylesData as Record<string, unknown>)?.footerAlign
          );

          setElementStyles(persistedElementStyles);
          
          // Footer ayarlarını set et
          setFooterSettings({
            backgroundColor: response.data.backgroundColor || '#1f2937',
            textColor: response.data.textColor || '#ffffff',
            textOpacity: 0.8,
            companyName: response.data.companyName || '',
            copyrightText: response.data.copyrightText || '',
            contactTitle: response.data.contactTitle || 'Contact',
            quickLinksTitle: response.data.quickLinksTitle || 'Quick Access',
            showLegalColumn: response.data.showLegalColumn !== false,
            legalColumnTitle: response.data.legalColumnTitle || 'Yasal',
            socialLinks: socialLinksData,
            quickLinks: quickLinksData,
            quickAccessColumns: quickAccessColumnsData,
            socialIconSize: response.data.socialIconSize || 24,
            iconLayout: ((elementStylesData as Record<string, unknown>)?.iconLayout === 'grid' ? 'grid' : 'row') as FooterIconLayout,
            columnOrder: columnOrderData,
            showContactIcons: (elementStylesData as Record<string, unknown>)?.showContactIcons !== false,
            enableFooterAnimation: (elementStylesData as Record<string, unknown>)?.enableFooterAnimation !== false,
            footerAlign: resolveFooterAlign((elementStylesData as Record<string, unknown>)?.footerAlign),
            companyLogo: response.data.logoUrl || '',
            logoWidth: response.data.logoWidth || 150,
            logoHeight: response.data.logoHeight || 60,
            companyLogo2: response.data.companyLogo2 || '',
            logo2Width: response.data.logo2Width || 150,
            logo2Height: response.data.logo2Height || 60,
            // İletişim Bilgileri
            contactEmail: response.data.contactEmail || '',
            contactPhone: response.data.contactPhone || '',
            contactAddress: response.data.contactAddress || '',
            // Newsletter / Bülten Bölümü (DB alanları)
            showNewsletterSection: response.data.showNewsletterSection || false,
            newsletterTitle: response.data.newsletterTitle || '',
            newsletterDescription: response.data.newsletterDescription || '',
            // Newsletter ekstra alanları (ElementStylesJson içinden)
            newsletterButtonText: elementStylesData?.newsletter?.buttonText || t('ecommerceFooterSettings.newsletter.subscribe'),
            showGenderSelection: elementStylesData?.newsletter?.showGender !== false,
            newsletterImage: elementStylesData?.newsletter?.image || '',
            newsletterImageWidth: elementStylesData?.newsletter?.imageWidth || 400,
            newsletterImageHeight: elementStylesData?.newsletter?.imageHeight || 350,
            newsletterBgColor: elementStylesData?.newsletter?.bgColor || '#ffffff',
            newsletterImageSpacing: elementStylesData?.newsletter?.imageSpacing || 0,
            newsletterMobileImage: elementStylesData?.newsletter?.mobileImage || '',
            newsletterMobileImageWidth: elementStylesData?.newsletter?.mobileImageWidth || 280,
            newsletterMobileImageHeight: elementStylesData?.newsletter?.mobileImageHeight || 200,
            newsletterShowImageOnMobile: elementStylesData?.newsletter?.showImageOnMobile !== false,
            paymentLogosImages: Array.isArray(elementStylesData?.paymentLogos?.images)
              ? (elementStylesData.paymentLogos.images as unknown[]).filter((u): u is string => typeof u === 'string' && Boolean(u))
              : (elementStylesData?.paymentLogos?.image ? [String(elementStylesData.paymentLogos.image)] : []),
            paymentLogosWidth: Number(elementStylesData?.paymentLogos?.width) > 0
              ? Number(elementStylesData.paymentLogos.width)
              : 320,
            paymentLogosHeight: Number(elementStylesData?.paymentLogos?.height) > 0
              ? Number(elementStylesData.paymentLogos.height)
              : 56,
            paymentLogosAlign: (['left', 'center', 'right'] as const).includes(elementStylesData?.paymentLogos?.align)
              ? elementStylesData.paymentLogos.align
              : 'center',
            footerLayout: normalizeFooterLayout(response.data.footerLayout),
            legalEntityName: typeof (elementStylesData as { legalEntityName?: unknown })?.legalEntityName === 'string'
              ? String((elementStylesData as { legalEntityName?: string }).legalEntityName)
              : '',
          });
          
          // Şirket açıklamasını set et (boş string de geçerli bir değer olmalı)
          const hasCompanyDescriptionText =
            elementStylesData?.companyDescription &&
            Object.prototype.hasOwnProperty.call(elementStylesData.companyDescription, 'text');
          if (hasCompanyDescriptionText) {
            setTempDescription(String(elementStylesData.companyDescription?.text ?? ''));
          }
          
          // ✅ İlk yükleme tamamlandı, artık değişiklikleri kaydet
          setTimeout(() => {
            isInitialLoadRef.current = false;
          }, 500);
        } else {
          console.log('ℹ️ [FOOTER SETTINGS] Ayar bulunamadı, varsayılan değerler kullanılıyor');
          setTimeout(() => {
            isInitialLoadRef.current = false;
          }, 500);
        }
      } catch (error: any) {
        console.error('❌ [FOOTER SETTINGS] Ayarlar yüklenirken hata:', error);
      }
    };

    loadFooterSettingsFromSQL();
  }, [configId]);

  useEffect(() => {
    if (!configId || !companyId) {
      setContactPreview({ email: '', phone: '', address: '', workingHours: '' });
      return;
    }
    let cancelled = false;
    getContactSettings(companyId, configId)
      .then((response) => {
        if (cancelled || !response?.success || !response.data) return;
        const data = response.data;
        const next = {
          email: String(data.contactEmail || ''),
          phone: String(data.contactPhone || ''),
          address: String(data.contactAddress || ''),
          workingHours: String(data.contactWorkingHours || ''),
        };
        setContactPreview(next);
        setFooterSettings((prev) => ({
          ...prev,
          contactEmail: next.email,
          contactPhone: next.phone,
          contactAddress: next.address,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setContactPreview({ email: '', phone: '', address: '', workingHours: '' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [configId, companyId]);

  useEffect(() => {
    if (!configId) {
      setLegalPreviewDocs([]);
      return;
    }
    let cancelled = false;
    getLegalDocuments(configId)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const visible = list
          .filter((d: any) => d && (d.isActive !== false) && d.showInFooter !== false && (d.title || d.slug))
          .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((d: any) => ({ title: String(d.title || 'Belge') }));
        setLegalPreviewDocs(visible);
      })
      .catch(() => {
        if (!cancelled) setLegalPreviewDocs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [configId]);

  // Sağ tık menüsünü açan fonksiyon
  const handleContextMenu = (event: React.MouseEvent, target: keyof typeof elementStyles) => {
    event.preventDefault();
    event.stopPropagation(); // Event propagation'ı durdur
    setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4, target } : null);
  };

  // Menüyü kapat
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Menüden "Düzenle" tıklandığında modalı aç
  const handleEditClick = () => {
    if (contextMenu) {
      if (contextMenu.target === 'backgroundColor') {
        // Arka plan rengi için özel modal aç
        setTempBackgroundColor(footerSettings.backgroundColor);
        setIsBackgroundModalOpen(true);
      } else if (contextMenu.target === 'companyDescription') {
        // Şirket açıklaması için özel modal aç
        setIsDescriptionModalOpen(true);
      } else {
        // Diğer text elementler için normal modal aç
        setEditingElementKey(contextMenu.target);
        setIsModalOpen(true);
      }
      handleCloseContextMenu();
    }
  };

  // Modal'da stiller kaydedildiğinde
  const handleStyleSave = (newStyles: any) => {
    if (editingElementKey && editingElementKey !== 'backgroundColor') {
      const updatedStyles = {
        ...elementStyles,
        [editingElementKey]: newStyles,
      };
      setElementStyles(updatedStyles);
      setIsModalOpen(false);
      
      // SQL'e kaydet (debounce olmadan)
      saveFooterSettings({ elementStyles: updatedStyles });
    }
  };

  // Arka plan rengi kaydedildiğinde
  const handleBackgroundColorSave = () => {
    const updatedStyles = {
      ...elementStyles,
      backgroundColor: { backgroundColor: tempBackgroundColor }
    };
    setElementStyles(updatedStyles);
    setFooterSettings(prev => ({
      ...prev,
      backgroundColor: tempBackgroundColor
    }));
    setIsBackgroundModalOpen(false);
    
    // SQL'e kaydet (debounce olmadan)
    saveFooterSettings({ elementStyles: updatedStyles, backgroundColor: tempBackgroundColor });
  };

  const applyCompanyDescriptionText = (value: string, persist: boolean) => {
    if (value.length > 500) return;
    setTempDescription(value);
    const updatedStyles = {
      ...elementStylesRef.current,
      companyDescription: {
        ...elementStylesRef.current.companyDescription,
        text: value
      }
    };
    setElementStyles(updatedStyles);
    if (persist) {
      autoSaveFooterSettings({ elementStyles: updatedStyles });
    }
  };

  // Şirket açıklaması kaydedildiğinde
  const handleDescriptionSave = () => {
    const updatedStyles = {
      ...elementStyles,
      companyDescription: {
        ...tempDescriptionStyles,
        text: tempDescription
      }
    };
    setElementStyles(updatedStyles);
    
    // CompanyDescription'ı footerSettings'e de kaydet
    const updatedFooterSettings = {
      ...footerSettings,
      companyDescription: tempDescription
    };
    setFooterSettings(updatedFooterSettings);
    setIsDescriptionModalOpen(false);
    
    // SQL'e kaydet (debounce olmadan) - sadece elementStyles gönder
    saveFooterSettings({ elementStyles: updatedStyles });
  };

  // ✅ AUTO-SAVE: Otomatik kaydetme fonksiyonu (debounce ile)
  const autoSaveFooterSettings = (settingsToSave?: Partial<typeof footerSettings> & { elementStyles?: typeof elementStyles }) => {
    if (!companyId || !configId) {
      console.log('⚠️ [FOOTER AUTO-SAVE] CompanyId veya ConfigId yok, kayıt atlanıyor', { companyId, configId });
      return;
    }

    // Önceki timer'ı iptal et
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Yeni timer başlat (1 saniye sonra kaydet)
    autoSaveTimerRef.current = setTimeout(async () => {
      console.log('💾 [FOOTER AUTO-SAVE] Otomatik kayıt başlatılıyor...');
      await saveFooterSettings(settingsToSave);
    }, 1000);
  };

  // ✅ Footer stillerini SQL'e kaydet
  const saveFooterSettings = async (settingsToSave?: Partial<typeof footerSettings> & { elementStyles?: typeof elementStyles }) => {
    if (!companyId || !configId) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.companyOrConfigMissing'),
        severity: 'error'
      });
      return;
    }
    
    console.log('💾 [FOOTER SETTINGS] Kaydedilecek ayarlar:', settingsToSave);
    console.log('💾 [FOOTER SETTINGS] Mevcut footerSettings:', footerSettings);
    console.log('💾 [FOOTER SETTINGS] Mevcut elementStyles:', elementStyles);
    
    setIsSaving(true);
    try {
      // JSON alanlarını stringify et
      const baseElementStyles = settingsToSave?.elementStyles || elementStylesRef.current;
      const currentFs = { ...footerSettingsRef.current, ...settingsToSave };
      const quickAccessColumnsToSave = currentFs.quickAccessColumns;
      const persistQuickAccessColumns = shouldPersistQuickAccessColumns(quickAccessColumnsToSave ?? []);
      const elementStylesToSave = {
        ...baseElementStyles,
        newsletter: {
          buttonText: currentFs.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe'),
          showGender: currentFs.showGenderSelection !== false,
          image: currentFs.newsletterImage || '',
          imageWidth: currentFs.newsletterImageWidth || 400,
          imageHeight: currentFs.newsletterImageHeight || 350,
          bgColor: currentFs.newsletterBgColor || '#ffffff',
          imageSpacing: currentFs.newsletterImageSpacing || 0,
          mobileImage: currentFs.newsletterMobileImage || '',
          mobileImageWidth: currentFs.newsletterMobileImageWidth || 280,
          mobileImageHeight: currentFs.newsletterMobileImageHeight || 200,
          showImageOnMobile: currentFs.newsletterShowImageOnMobile !== false,
        },
        paymentLogos: {
          images: Array.isArray(currentFs.paymentLogosImages)
            ? currentFs.paymentLogosImages.filter(Boolean)
            : [],
          width: Number(currentFs.paymentLogosWidth) > 0 ? Number(currentFs.paymentLogosWidth) : 320,
          height: Number(currentFs.paymentLogosHeight) > 0 ? Number(currentFs.paymentLogosHeight) : 56,
          align: currentFs.paymentLogosAlign === 'left' || currentFs.paymentLogosAlign === 'right'
            ? currentFs.paymentLogosAlign
            : 'center',
        },
      } as typeof elementStyles & { quickAccessColumns?: QuickAccessColumn[] };

      if (persistQuickAccessColumns) {
        elementStylesToSave.quickAccessColumns = quickAccessColumnsToSave;
      } else {
        delete elementStylesToSave.quickAccessColumns;
      }
      (elementStylesToSave as Record<string, unknown>).columnOrder = resolveFooterColumnOrder(
        currentFs.columnOrder,
        quickAccessColumnsToSave,
        { includeLegal: currentFs.showLegalColumn !== false }
      );
      (elementStylesToSave as Record<string, unknown>).iconLayout = currentFs.iconLayout || 'row';
      (elementStylesToSave as Record<string, unknown>).showContactIcons = currentFs.showContactIcons !== false;
      (elementStylesToSave as Record<string, unknown>).enableFooterAnimation = currentFs.enableFooterAnimation !== false;
      (elementStylesToSave as Record<string, unknown>).footerAlign = resolveFooterAlign(currentFs.footerAlign);
      (elementStylesToSave as Record<string, unknown>).legalEntityName = String(currentFs.legalEntityName || '').trim();
      (elementStylesToSave as Record<string, unknown>).footerLayout = normalizeFooterLayout(currentFs.footerLayout);
      const socialLinksToSave = currentFs.socialLinks;
      const quickLinksToSave = currentFs.quickLinks;
      
      const elementStylesJson = JSON.stringify(elementStylesToSave);
      const socialLinksJson = JSON.stringify(socialLinksToSave);
      const quickLinksJson = JSON.stringify(quickLinksToSave);
      const quickAccessColumnsJson = JSON.stringify(quickAccessColumnsToSave ?? []);
      
      const data = {
        id: footerSettingsId,
        companyId: companyId,
        configId: configId,
        showFooter: true,
        footerLayout: normalizeFooterLayout(currentFs.footerLayout),
        columnCount: 3,
        backgroundColor: settingsToSave?.backgroundColor || footerSettings.backgroundColor,
        textColor: settingsToSave?.textColor || footerSettings.textColor,
        linkColor: elementStylesToSave.quickLinks?.color || 'rgba(255, 255, 255, 0.8)',
        showLogo: !!(settingsToSave?.companyLogo || footerSettings.companyLogo),
        logoUrl: settingsToSave?.companyLogo || footerSettings.companyLogo || null,
        logoWidth: settingsToSave?.logoWidth || footerSettings.logoWidth,
        logoHeight: settingsToSave?.logoHeight || footerSettings.logoHeight,
        companyLogo2: settingsToSave?.companyLogo2 || footerSettings.companyLogo2 || null,
        logo2Width: settingsToSave?.logo2Width || footerSettings.logo2Width,
        logo2Height: settingsToSave?.logo2Height || footerSettings.logo2Height,
        showAboutSection: true,
        aboutTitle: t('ecommerceFooterSettings.preview.aboutUs'),
        aboutText: elementStylesToSave.companyDescription?.text ?? null,
        showContactSection: true,
        contactTitle: settingsToSave?.contactTitle || footerSettings.contactTitle,
        showContactEmail: true,
        showContactPhone: true,
        showContactAddress: true,
        // İletişim Bilgileri
        contactEmail: settingsToSave?.contactEmail || footerSettings.contactEmail || null,
        contactPhone: settingsToSave?.contactPhone || footerSettings.contactPhone || null,
        contactAddress: settingsToSave?.contactAddress || footerSettings.contactAddress || null,
        showSocialLinks: socialLinksToSave.length > 0,
        socialLinksTitle: t('ecommerceFooterSettings.socialMedia.title'),
        socialIconSize: settingsToSave?.socialIconSize ?? footerSettings.socialIconSize,
        showQuickLinks: true,
        quickLinksTitle: settingsToSave?.quickLinksTitle || footerSettings.quickLinksTitle,
        showLegalColumn: (settingsToSave?.showLegalColumn ?? footerSettings.showLegalColumn) !== false,
        legalColumnTitle: settingsToSave?.legalColumnTitle || footerSettings.legalColumnTitle || 'Yasal',
        showNewsletterSection: currentFs.showNewsletterSection ?? false,
        newsletterTitle: currentFs.newsletterTitle || null,
        newsletterDescription: currentFs.newsletterDescription || null,
        showBottomBar: true,
        bottomBarBackgroundColor: settingsToSave?.backgroundColor || footerSettings.backgroundColor,
        copyrightText: settingsToSave?.copyrightText || footerSettings.copyrightText || null,
        showPaymentIcons: (currentFs.paymentLogosImages || []).length > 0,
        companyName: settingsToSave?.companyName || footerSettings.companyName || null,
        companyDescription: elementStylesToSave.companyDescription?.text ?? null,
        elementStylesJson: elementStylesJson,
        socialLinksJson: socialLinksJson,
        quickLinksJson: quickLinksJson,
        quickAccessColumnsJson: quickAccessColumnsJson,
      };
      
      const response = await saveFooterSettingsAPI(data);
      
      if (response.success) {
        console.log('✅ [FOOTER SETTINGS] Ayarlar başarıyla kaydedildi');
        if (!footerSettingsId && response.data?.id) {
          setFooterSettingsId(response.data.id);
        }

        setElementStyles(elementStylesToSave);
        
        notifyEcommerceSaved(t('ecommerceGeneral.autoSave.savedToast'));
      }
    } catch (error) {
      console.error('❌ [FOOTER SETTINGS] Footer ayarları kaydedilirken hata:', error);
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.saveError'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Genel yazı rengini değiştir
  const handleGlobalTextColorChange = (color: string) => {
    const updatedStyles = {
      ...elementStyles,
      companyName: { ...elementStyles.companyName, color },
      companyDescription: { ...elementStyles.companyDescription, color },
      sectionTitle: { ...elementStyles.sectionTitle, color },
      contactInfo: { ...elementStyles.contactInfo, color },
      quickLinks: { ...elementStyles.quickLinks, color },
      copyright: { ...elementStyles.copyright, color }
    };
    setElementStyles(updatedStyles);
    
    // ✅ AUTO-SAVE
    autoSaveFooterSettings({ elementStyles: updatedStyles });
  };

  // Genel arka plan rengini değiştir
  const handleGlobalBackgroundColorChange = (color: string) => {
    console.log('🎨 [FOOTER SETTINGS] Arka plan rengi degistiriliyor:', color);
    const updatedFooterSettings = { ...footerSettings, backgroundColor: color };
    const updatedElementStyles = {
      ...elementStyles,
      backgroundColor: { backgroundColor: color }
    };
    
    setFooterSettings(updatedFooterSettings);
    setElementStyles(updatedElementStyles);
    
    // ✅ AUTO-SAVE
    autoSaveFooterSettings({ 
      ...updatedFooterSettings,
      elementStyles: updatedElementStyles 
    });
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const SocialIcon = ({ platform, icon, size }: { platform: string, icon: string, size?: number }) => (
    <PanelSocialIcon platform={platform} icon={icon} size={size || footerSettings.socialIconSize} />
  );

  const resolvedColumnOrder = resolveFooterColumnOrder(
    footerSettings.columnOrder,
    footerSettings.quickAccessColumns,
    { includeLegal: footerSettings.showLegalColumn !== false }
  );
  const columnsBySide = splitFooterColumnsBySide(resolvedColumnOrder);
  const previewColumnOrder = [...columnsBySide.left, ...columnsBySide.right];
  const isCorporateLayout = footerSettings.footerLayout === 'layout2';
  const firstRightColumnKey = columnsBySide.right[0]
    ? getFooterColumnKey(columnsBySide.right[0])
    : null;

  const getColumnOrderLabel = (slot: FooterColumnSlot) => {
    if (slot.type === 'brand') return t('ecommerceFooterSettings.columnOrder.brand');
    if (slot.type === 'contact') return t('ecommerceFooterSettings.columnOrder.contact');
    if (slot.type === 'campaigns') return t('ecommerceFooterSettings.columnOrder.campaigns');
    if (slot.type === 'legal') {
      return (footerSettings.legalColumnTitle || '').trim()
        || t('ecommerceFooterSettings.columnOrder.legal');
    }
    const columnIndex = footerSettings.quickAccessColumns.findIndex((column) => column.id === slot.id);
    const column = footerSettings.quickAccessColumns[columnIndex];
    if (!column) return t('ecommerceFooterSettings.quickAccess.columnLabel', { number: slot.id });
    return column.title?.trim() || t('ecommerceFooterSettings.quickAccess.columnLabel', { number: columnIndex + 1 });
  };

  const handleMoveColumn = (slot: FooterColumnSlot, direction: -1 | 1) => {
    const nextOrder = moveFooterColumnInSide(resolvedColumnOrder, getFooterColumnKey(slot), direction);
    const updatedFooterSettings = { ...footerSettings, columnOrder: nextOrder };
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
  };

  const handleColumnSideChange = (slot: FooterColumnSlot, side: FooterColumnSide) => {
    const nextOrder = setFooterColumnSide(resolvedColumnOrder, getFooterColumnKey(slot), side);
    const updatedFooterSettings = { ...footerSettings, columnOrder: nextOrder };
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
  };

  // Sosyal Medya Modalını aç/kapa
  const handleOpenSocialModal = (link: SocialLink | null = null) => {
    setEditingSocialLink(link);
    setIsSocialModalOpen(true);
  };

  const handleCloseSocialModal = () => {
    setIsSocialModalOpen(false);
    setEditingSocialLink(null);
  };

  // Sosyal Medya linkini kaydet/güncelle
  const handleSaveSocialLink = (link: SocialLink) => {
    const updatedLinks = editingSocialLink
      ? footerSettings.socialLinks.map(l => l.id === link.id ? link : l)
      : [...footerSettings.socialLinks, link];
    
    const updatedFooterSettings = { ...footerSettings, socialLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece sosyal linkleri güncelle
    saveFooterSettings(updatedFooterSettings);
    handleCloseSocialModal();
  };

  // Silme modalını aç
  const openDeleteConfirm = (id: string) => {
    setDeletingSocialLinkId(id);
    setIsDeleteConfirmOpen(true);
  };

  // Silme modalını kapat
  const closeDeleteConfirm = () => {
    setDeletingSocialLinkId(null);
    setIsDeleteConfirmOpen(false);
  };

  // Sosyal Medya linkini sil
  const handleDeleteSocialLink = () => {
    if (!deletingSocialLinkId) return;
    const updatedLinks = footerSettings.socialLinks.filter(l => l.id !== deletingSocialLinkId);
    const updatedFooterSettings = { ...footerSettings, socialLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece sosyal linkleri güncelle
    saveFooterSettings(updatedFooterSettings);
    closeDeleteConfirm();
  };


  const getColumnTitle = (column: QuickAccessColumn, index: number) =>
    column.title?.trim() || t('ecommerceFooterSettings.quickAccess.columnLabel', { number: index + 1 });

  const getPreviewQuickAccessHeader = (column: QuickAccessColumn, columnIndex: number) =>
    getQuickAccessColumnHeader(
      column,
      columnIndex,
      footerSettings.quickAccessColumns,
      footerSettings.quickLinksTitle || 'Hızlı Erişim'
    );

  // Hızlı Erişim Linki için Modal Aç/Kapa
  const handleOpenQuickLinkModal = (link: QuickLink | null = null, defaultColumn?: number) => {
    setEditingQuickLink(link);
    setQuickLinkDefaultColumn(link ? undefined : defaultColumn);
    setIsQuickLinkModalOpen(true);
  };

  const handleCloseQuickLinkModal = () => {
    setIsQuickLinkModalOpen(false);
    setEditingQuickLink(null);
    setQuickLinkDefaultColumn(undefined);
  };

  const handleAddQuickAccessColumn = () => {
    const columns = footerSettings.quickAccessColumns;
    if (columns.length >= MAX_QUICK_ACCESS_COLUMNS) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.maxColumnsLimit', { count: MAX_QUICK_ACCESS_COLUMNS }),
        severity: 'error',
      });
      return;
    }

    const nextId = columns.reduce((max, col) => Math.max(max, col.id), 0) + 1;
    const updatedColumns = [
      ...columns,
      {
        id: nextId,
        maxLinks: 10,
        hasFixedLinks: false,
      },
    ];
    const updatedFooterSettings = {
      ...footerSettings,
      quickAccessColumns: updatedColumns,
      columnOrder: addQuickAccessSlot(resolvedColumnOrder, nextId),
    };
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
  };

  const handleUpdateColumnTitle = (columnId: number, title: string) => {
    setFooterSettings(prev => {
      const updatedColumns = prev.quickAccessColumns.map(col =>
        col.id === columnId ? { ...col, title } : col
      );
      return { ...prev, quickAccessColumns: updatedColumns };
    });
  };

  const handleColumnTitleBlur = (columnId: number, title: string) => {
    setFooterSettings(prev => {
      const updatedColumns = prev.quickAccessColumns.map(col =>
        col.id === columnId ? { ...col, title } : col
      );
      const updatedElementStyles = {
        ...elementStylesRef.current,
        quickAccessColumns: updatedColumns,
      };
      setElementStyles(updatedElementStyles);
      saveFooterSettings({
        quickAccessColumns: updatedColumns,
        elementStyles: updatedElementStyles,
      });
      return { ...prev, quickAccessColumns: updatedColumns };
    });
  };

  const openColumnDeleteConfirm = (columnId: number) => {
    setDeletingColumnId(columnId);
    setIsColumnDeleteConfirmOpen(true);
  };

  const closeColumnDeleteConfirm = () => {
    setDeletingColumnId(null);
    setIsColumnDeleteConfirmOpen(false);
  };

  const handleDeleteQuickAccessColumn = () => {
    if (deletingColumnId == null) return;

    const columns = footerSettings.quickAccessColumns;
    const columnToDelete = columns.find(col => col.id === deletingColumnId);
    if (columnToDelete?.hasFixedLinks) {
      closeColumnDeleteConfirm();
      return;
    }

    if (columns.length <= 1) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.minColumnsRequired'),
        severity: 'error',
      });
      closeColumnDeleteConfirm();
      return;
    }

    const updatedColumns = columns.filter(col => col.id !== deletingColumnId);
    const updatedLinks = footerSettings.quickLinks.filter(link => link.column !== deletingColumnId);
    const updatedFooterSettings = {
      ...footerSettings,
      quickAccessColumns: updatedColumns,
      quickLinks: updatedLinks,
      columnOrder: removeQuickAccessSlot(resolvedColumnOrder, deletingColumnId),
    };
    footerSettingsRef.current = updatedFooterSettings;
    setFooterSettings(updatedFooterSettings);
    saveFooterSettings(updatedFooterSettings);
    closeColumnDeleteConfirm();
  };

  // Hızlı Erişim Linki Kaydet/Güncelle
  const handleSaveQuickLink = (link: QuickLink) => {
    const targetColumn = footerSettings.quickAccessColumns.find(col => col.id === link.column);
    if (!targetColumn) {
      setSnackbar({ open: true, message: t('ecommerceFooterSettings.quickAccess.invalidColumn'), severity: 'error' });
      return;
    }

    const columnCount = footerSettings.quickLinks.filter(
      l => l.column === link.column && l.id !== link.id
    ).length;

    if (columnCount >= targetColumn.maxLinks) {
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.quickAccess.columnLimit', { count: targetColumn.maxLinks }),
        severity: 'error',
      });
      return;
    }

    const updatedLinks = editingQuickLink
      ? footerSettings.quickLinks.map(l => l.id === link.id ? link : l)
      : [...footerSettings.quickLinks, link];
    
    const updatedFooterSettings = { ...footerSettings, quickLinks: updatedLinks };
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece hızlı erişim linklerini güncelle
    saveFooterSettings(updatedFooterSettings);
    handleCloseQuickLinkModal();
  };

  // Hızlı Erişim Linki Silme Onayı
  const openQuickLinkDeleteConfirm = (id: string) => {
    setDeletingQuickLinkId(id);
    setIsQuickLinkDeleteConfirmOpen(true);
  };

  const closeQuickLinkDeleteConfirm = () => {
    setDeletingQuickLinkId(null);
    setIsQuickLinkDeleteConfirmOpen(false);
  };

  // Hızlı Erişim Linkini Sil
  const handleDeleteQuickLink = () => {
    if (!deletingQuickLinkId) return;
    const updatedLinks = footerSettings.quickLinks.filter(l => l.id !== deletingQuickLinkId);
    const updatedFooterSettings = { ...footerSettings, quickLinks: updatedLinks };
    footerSettingsRef.current = updatedFooterSettings;
    setFooterSettings(updatedFooterSettings);
    // Tüm ayarları koru, sadece hızlı erişim linklerini güncelle
    saveFooterSettings(updatedFooterSettings);
    closeQuickLinkDeleteConfirm();
  };

  // ✅ YENİ: Resim kütüphanesi modalını aç
  const handleOpenImageLibrary = (logoType: FooterImageSlot) => {
    setCurrentLogoType(logoType);
    setIsImageLibraryOpen(true);
  };

  // ✅ YENİ: Resim kütüphanesinden resim seçildiğinde
  const handleImageSelect = async (selectedUrls: string[]) => {
    if (selectedUrls.length === 0) return;
    
    const imageUrl = selectedUrls[0];
    console.log('🖼️ [FOOTER SETTINGS] Resim seçildi:', imageUrl, 'Tip:', currentLogoType);

    let updatedSettings = footerSettings;
    if (currentLogoType === 'paymentLogos') {
      const existing = footerSettings.paymentLogosImages || [];
      const merged = [...existing];
      selectedUrls.forEach((url) => {
        if (url && !merged.includes(url)) merged.push(url);
      });
      updatedSettings = { ...footerSettings, paymentLogosImages: merged.slice(0, 8) };
    } else {
      const fieldMap: Record<string, string> = {
        logo1: 'companyLogo',
        logo2: 'companyLogo2',
        newsletter: 'newsletterImage',
        newsletterMobile: 'newsletterMobileImage',
      };
      const field = fieldMap[currentLogoType] || 'companyLogo';
      updatedSettings = { ...footerSettings, [field]: imageUrl };
    }

    setFooterSettings(updatedSettings);
    setIsImageLibraryOpen(false);
    
    await saveFooterSettings(updatedSettings);
    
    const messages: Record<string, string> = {
      logo1: t('ecommerceFooterSettings.messages.logoSelected'),
      logo2: t('ecommerceFooterSettings.messages.secondLogoSelected'),
      newsletter: t('ecommerceFooterSettings.messages.newsletterImageSelected'),
      newsletterMobile: t('ecommerceFooterSettings.messages.newsletterMobileImageSelected'),
      paymentLogos: t('ecommerceFooterSettings.messages.paymentLogosSelected'),
    };
    setSnackbar({
      open: true,
      message: messages[currentLogoType] || t('ecommerceFooterSettings.messages.imageSelected'),
      severity: 'success'
    });
  };

  const mergePaymentLogoUrls = (urls: string[]) => {
    const existing = footerSettings.paymentLogosImages || [];
    const merged = [...existing];
    urls.forEach((url) => {
      if (url && !merged.includes(url)) merged.push(url);
    });
    return merged.slice(0, 8);
  };

  const handlePaymentFilesUpload = async (files: FileList | File[]) => {
    const list = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (list.length === 0) return;
    const remaining = Math.max(0, 8 - (footerSettings.paymentLogosImages || []).length);
    if (remaining === 0) return;
    try {
      setPaymentUploading(true);
      const uploaded = await ImageUploadService.uploadImages(list.slice(0, remaining));
      const urls = uploaded.map((item) => item.originalUrl).filter(Boolean);
      if (urls.length === 0) return;
      const updatedSettings = {
        ...footerSettings,
        paymentLogosImages: mergePaymentLogoUrls(urls),
      };
      setFooterSettings(updatedSettings);
      await saveFooterSettings(updatedSettings);
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.paymentLogosSelected'),
        severity: 'success',
      });
    } catch (error) {
      console.error('Payment logo upload failed:', error);
      setSnackbar({
        open: true,
        message: t('ecommerceFooterSettings.messages.paymentLogosUploadError', {
          defaultValue: 'Ödeme logosu yüklenemedi.',
        }),
        severity: 'error',
      });
    } finally {
      setPaymentUploading(false);
    }
  };

  const handleRemovePaymentLogo = async (url: string) => {
    const updatedSettings = {
      ...footerSettings,
      paymentLogosImages: (footerSettings.paymentLogosImages || []).filter((item) => item !== url)
    };
    setFooterSettings(updatedSettings);
    await saveFooterSettings(updatedSettings);
    setSnackbar({
      open: true,
      message: t('ecommerceFooterSettings.messages.paymentLogoDeleted'),
      severity: 'success'
    });
  };

  // Logo sil
  const handleRemoveLogo = async () => {
    if (!footerSettings.companyLogo || !companyId) return;

    // Footer ayarlarını güncelle
    const updatedSettings = {
      ...footerSettings,
      companyLogo: ''
    };
    
    setFooterSettings(updatedSettings);
    
    // SQL'e kaydet
    await saveFooterSettings(updatedSettings);
    
    setSnackbar({
      open: true,
      message: t('ecommerceFooterSettings.messages.logoDeleted'),
      severity: 'success'
    });
  };

  // İkinci logo sil
  const handleRemoveLogo2 = async () => {
    if (!footerSettings.companyLogo2 || !companyId) return;

    // Footer ayarlarını güncelle
    const updatedSettings = {
      ...footerSettings,
      companyLogo2: ''
    };
    
    setFooterSettings(updatedSettings);
    
    // SQL'e kaydet
    await saveFooterSettings(updatedSettings);
    
    setSnackbar({
      open: true,
      message: t('ecommerceFooterSettings.messages.secondLogoDeleted'),
      severity: 'success'
    });
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <SectionTitle sx={{ mb: 0 }}>
          <SettingsIcon /> {t('ecommerceFooterSettings.title')}
          <GuideButton guideKey="ecommerce.footer" size="small" sx={{ ml: 0.5 }} />
        </SectionTitle>
        
        {/* ✅ AUTO-SAVE: Kayıt durumu göstergesi */}
        {isSaving && (
          <Typography variant="body2" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>💾</span> {t('ecommerceFooterSettings.messages.saving')}
          </Typography>
        )}
      </Box>
      
      <Box>
        {/* Footer Önizlemesi */}
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          {t('ecommerceFooterSettings.preview.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Footer elementleri üzerinde sağ tık yaparak stilleri düzenleyebilirsiniz.
        </Typography>

        {/* Genel Footer Ayarları */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('ecommerceFooterSettings.templates.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('ecommerceFooterSettings.templates.hint')}
          </Typography>
          <FooterTemplatePicker
            value={footerSettings.footerLayout}
            classicTitle={t('ecommerceFooterSettings.templates.classic')}
            classicHint={t('ecommerceFooterSettings.templates.classicHint')}
            corporateTitle={t('ecommerceFooterSettings.templates.corporate')}
            corporateHint={t('ecommerceFooterSettings.templates.corporateHint')}
            onChange={(next) => {
              const updated = { ...footerSettings, footerLayout: next };
              setFooterSettings(updated);
              autoSaveFooterSettings(updated);
            }}
          />
          {isCorporateLayout && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={t('ecommerceFooterSettings.templates.legalEntity')}
              placeholder={t('ecommerceFooterSettings.templates.legalEntityPlaceholder')}
              helperText={t('ecommerceFooterSettings.templates.legalEntityHelper')}
              value={footerSettings.legalEntityName}
              onChange={(e) => {
                const updated = { ...footerSettings, legalEntityName: e.target.value.slice(0, 200) };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              inputProps={{ maxLength: 200 }}
            />
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>
            {t('ecommerceFooterSettings.generalSettings.title')}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            {/* Genel Yazı Rengi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: '100px' }}>
                {t('ecommerceFooterSettings.generalSettings.textColor')}:
              </Typography>
              <Box
                component="input"
                type="color"
                value={elementStyles.sectionTitle.color}
                onChange={(e: any) => handleGlobalTextColorChange(e.target.value)}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={elementStyles.sectionTitle.color}
                onChange={(e: any) => handleGlobalTextColorChange(e.target.value)}
                placeholder="#ffffff"
                sx={{ 
                  width: '120px',
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1,
                  fontSize: '14px'
                }}
              />
            </Box>

            {/* Genel Arka Plan Rengi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ minWidth: '100px' }}>
                {t('ecommerceFooterSettings.generalSettings.background')}:
              </Typography>
              <Box
                component="input"
                type="color"
                value={footerSettings.backgroundColor}
                onChange={(e: any) => handleGlobalBackgroundColorChange(e.target.value)}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={footerSettings.backgroundColor}
                onChange={(e: any) => handleGlobalBackgroundColorChange(e.target.value)}
                placeholder="#1f2937"
                sx={{ 
                  width: '120px',
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1,
                  fontSize: '14px'
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Typography variant="body2">{t('ecommerceFooterSettings.generalSettings.animation')}:</Typography>
            <Box
              component="input"
              type="checkbox"
              checked={footerSettings.enableFooterAnimation}
              onChange={(e: any) => {
                const updated = { ...footerSettings, enableFooterAnimation: e.target.checked };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              sx={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceFooterSettings.generalSettings.animationHint')}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Bu ayarlar tüm footer yazılarının rengini aynı anda değiştirir. İsterseniz her elementi sağ tık ile ayrı ayrı da düzenleyebilirsiniz.
          </Typography>
        </Paper>
        
        {/* Footer Örneği - Birebir aynısı */}
        <Box 
          sx={{ 
            bgcolor: footerSettings.backgroundColor,
            color: '#ffffff',
            py: 4,
            px: 2,
            borderRadius: 1,
            mb: 3,
            cursor: 'context-menu'
          }}
          onContextMenu={(e) => handleContextMenu(e, 'backgroundColor')}
        >
          <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
            <Box sx={isCorporateLayout ? {
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: previewColumnOrder.length <= 1
                  ? '1fr'
                  : `minmax(180px, 1.45fr) repeat(${Math.max(previewColumnOrder.length - 1, 1)}, minmax(0, 1fr))`,
              },
              alignItems: 'flex-start',
              gap: 3,
            } : {
              display: 'flex',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              justifyContent: columnsBySide.left.length && columnsBySide.right.length
                ? 'space-between'
                : columnsBySide.right.length
                  ? 'flex-end'
                  : 'flex-start',
              alignItems: 'flex-start',
              gap: 4
            }}>
              {previewColumnOrder.map((slot) => {
                const pushToRight = !isCorporateLayout && firstRightColumnKey === getFooterColumnKey(slot);
                const arrowLinks = isCorporateLayout && slot.type !== 'brand' && slot.type !== 'contact';
                const previewColumnSx = {
                  ...(isCorporateLayout
                    ? { minWidth: 0 }
                    : { flex: '0 0 200px', width: 200, maxWidth: 240 }),
                  textAlign: FOOTER_COLUMN_CONTENT_ALIGN.textAlign,
                  ml: pushToRight ? 'auto' : undefined,
                  ...(arrowLinks ? {
                    '& a::before': {
                      content: '"→"',
                      display: 'inline-block',
                      width: '1rem',
                      opacity: 0.65,
                    },
                  } : {}),
                };
                if (slot.type === 'brand') {
                  return (
                    <Box key="brand" sx={previewColumnSx}>
                      {footerSettings.companyLogo ? (
                        <Box sx={{ mb: 2, cursor: 'context-menu' }} onContextMenu={(e) => handleContextMenu(e, 'companyName')}>
                          <img
                            src={footerSettings.companyLogo}
                            alt={exampleCompany.name}
                            style={{
                              width: `${footerSettings.logoWidth}px`,
                              maxWidth: '100%',
                              height: `${footerSettings.logoHeight}px`,
                              objectFit: 'contain',
                              display: 'block'
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            ...elementStyles.companyName,
                            cursor: 'context-menu',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'companyName')}
                        >
                          {exampleCompany.name}
                        </Typography>
                      )}

                      {footerSettings.companyLogo2 && (
                        <Box sx={{ mb: 2, cursor: 'context-menu' }} onContextMenu={(e) => handleContextMenu(e, 'companyName')}>
                          <img
                            src={footerSettings.companyLogo2}
                            alt={`${exampleCompany.name} - Second Logo`}
                            style={{
                              width: `${footerSettings.logo2Width}px`,
                              maxWidth: '100%',
                              height: `${footerSettings.logo2Height}px`,
                              objectFit: 'contain',
                              display: 'block'
                            }}
                          />
                        </Box>
                      )}
                      {tempDescription ? (
                        <Typography
                          variant="body2"
                          sx={{
                            ...elementStyles.companyDescription,
                            cursor: 'context-menu',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'companyDescription')}
                        >
                          {tempDescription}
                        </Typography>
                      ) : null}

                      {footerSettings.paymentLogosImages.length > 0 && (
                        <Box sx={{
                          mt: 1.5,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: footerSettings.paymentLogosAlign === 'left'
                            ? 'flex-start'
                            : footerSettings.paymentLogosAlign === 'right'
                              ? 'flex-end'
                              : 'center',
                          gap: 1,
                        }}>
                          {footerSettings.paymentLogosImages.map((src) => (
                            <img
                              key={src}
                              src={src}
                              alt=""
                              style={{
                                width: `${footerSettings.paymentLogosWidth}px`,
                                maxWidth: '100%',
                                height: `${footerSettings.paymentLogosHeight}px`,
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                          ))}
                        </Box>
                      )}

                      <Box sx={{
                        mt: 2,
                        display: footerSettings.iconLayout === 'grid' ? 'grid' : 'flex',
                        gridTemplateColumns: footerSettings.iconLayout === 'grid' ? 'repeat(3, max-content)' : undefined,
                        flexWrap: 'wrap',
                        gap: 1,
                        alignItems: 'center'
                      }}>
                        {footerSettings.socialLinks.map((link) => (
                          <Box
                            key={link.id}
                            sx={{
                              color: elementStyles.quickLinks.color,
                              width: footerSettings.socialIconSize,
                              height: footerSettings.socialIconSize,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <SocialIcon platform={link.platform} icon={link.icon} size={footerSettings.socialIconSize} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                }

                if (slot.type === 'contact') {
                  const contactIconSize = Math.max(16, Math.round(footerSettings.socialIconSize * 0.75));
                  return (
                    <Box key="contact" sx={previewColumnSx}>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          ...elementStyles.sectionTitle,
                          cursor: 'context-menu',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, 'sectionTitle')}
                      >
                        İletişim
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            ...elementStyles.contactInfo,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            cursor: 'context-menu',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                        >
                          {footerSettings.showContactIcons && <PlaceOutlinedIcon sx={{ fontSize: contactIconSize, mt: 0.2 }} />}
                          {contactPreview.address || footerSettings.contactAddress || exampleCompany.address}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            ...elementStyles.contactInfo,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'context-menu',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                        >
                          {footerSettings.showContactIcons && <PhoneAndroidIcon sx={{ fontSize: contactIconSize }} />}
                          {contactPreview.phone || footerSettings.contactPhone || exampleCompany.phone}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            ...elementStyles.contactInfo,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'context-menu',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                        >
                          {footerSettings.showContactIcons && <MailOutlineIcon sx={{ fontSize: contactIconSize }} />}
                          {contactPreview.email || footerSettings.contactEmail || exampleCompany.email}
                        </Typography>
                        {(contactPreview.workingHours) && (
                          <Typography
                            variant="body2"
                            sx={{
                              ...elementStyles.contactInfo,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1,
                              whiteSpace: 'pre-line',
                              cursor: 'context-menu',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onContextMenu={(e) => handleContextMenu(e, 'contactInfo')}
                          >
                            {footerSettings.showContactIcons && <AccessTimeOutlinedIcon sx={{ fontSize: contactIconSize, mt: 0.2 }} />}
                            {contactPreview.workingHours}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                }

                if (slot.type === 'legal') {
                  const legalTitle = (footerSettings.legalColumnTitle || '').trim() || t('ecommerceFooterSettings.columnOrder.legal');
                  const previewLinks = legalPreviewDocs.length > 0
                    ? legalPreviewDocs
                    : [
                        { title: 'Çerez Politikası' },
                        { title: 'KVKK Aydınlatma Metni' },
                        { title: 'Gizlilik Sözleşmesi' },
                      ];
                  return (
                    <Box key="legal" sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...previewColumnSx }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          ...elementStyles.sectionTitle,
                          fontWeight: 'bold',
                          cursor: 'context-menu',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, 'sectionTitle')}
                      >
                        {legalTitle}
                      </Typography>
                      {previewLinks.map((doc) => (
                        <Link
                          key={doc.title}
                          href="#"
                          sx={{
                            ...elementStyles.quickLinks,
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            cursor: 'context-menu',
                            '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                        >
                          {doc.title}
                        </Link>
                      ))}
                    </Box>
                  );
                }

                if (slot.type === 'campaigns') {
                  return (
                    <Box key="campaigns" sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...previewColumnSx }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          ...elementStyles.sectionTitle,
                          fontWeight: 'bold',
                        }}
                      >
                        {t('ecommerceFooterSettings.columnOrder.campaigns')}
                      </Typography>
                    </Box>
                  );
                }

                const column = footerSettings.quickAccessColumns.find((item) => item.id === slot.id);
                if (!column) return null;
                const columnIndex = footerSettings.quickAccessColumns.findIndex((item) => item.id === column.id);
                const columnLinks = footerSettings.quickLinks.filter(l => l.column === column.id);
                const columnHeader = getPreviewQuickAccessHeader(column, columnIndex);
                return (
                  <Box key={`quickAccess-${column.id}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, ...previewColumnSx }}>
                    {columnHeader && (
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 0.5,
                        ...elementStyles.sectionTitle,
                        fontWeight: 'bold',
                        cursor: 'context-menu',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onContextMenu={(e) => handleContextMenu(e, 'sectionTitle')}
                    >
                      {columnHeader}
                    </Typography>
                    )}
                    {column.hasFixedLinks && (
                      <>
                        <Link
                          href="#"
                          sx={{
                            ...elementStyles.quickLinks,
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            cursor: 'context-menu',
                            '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                        >
                          Ana Sayfa
                        </Link>
                        <Link
                          href="#"
                          sx={{
                            ...elementStyles.quickLinks,
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            cursor: 'context-menu',
                            '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                        >
                          Hakkımızda
                        </Link>
                        <Link
                          href="#"
                          sx={{
                            ...elementStyles.quickLinks,
                            textDecoration: 'none',
                            transition: 'color 0.2s',
                            cursor: 'context-menu',
                            '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                        >
                          İletişim
                        </Link>
                      </>
                    )}
                    {columnLinks.map(link => (
                      <Link
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          ...elementStyles.quickLinks,
                          textDecoration: 'none',
                          transition: 'color 0.2s',
                          cursor: 'context-menu',
                          '&:hover': { color: '#ffffff', textDecoration: 'underline' }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, 'quickLinks')}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </Box>
                );
              })}
            </Box>
            
            {isCorporateLayout && (
              <Box sx={{
                mt: 4,
                pt: 2.5,
                borderTop: '1px solid rgba(255,255,255,0.14)',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
              }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 4 } }}>
                  {[
                    {
                      icon: <LockOutlinedIcon sx={{ fontSize: 16 }} />,
                      title: t('ecommerceFooterSettings.templates.sslTitle'),
                      text: t('ecommerceFooterSettings.templates.sslText'),
                    },
                    {
                      icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />,
                      title: t('ecommerceFooterSettings.templates.paymentTitle'),
                      text: t('ecommerceFooterSettings.templates.paymentText'),
                    },
                  ].map((item) => (
                    <Box key={item.title} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.35)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, ...elementStyles.sectionTitle, fontSize: '0.85rem' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7, ...elementStyles.quickLinks, fontSize: '0.72rem' }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 0.2 }}>
                  {t('ecommerceFooterSettings.templates.poweredBy')}
                </Typography>
              </Box>
            )}

            {/* Copyright */}
            <Box sx={{ 
              mt: isCorporateLayout ? 2.5 : 4, 
              pt: isCorporateLayout ? 2 : 3, 
              borderTop: '1px solid rgba(255,255,255,0.2)', 
              textAlign: isCorporateLayout ? 'left' : 'center',
              display: isCorporateLayout ? 'flex' : 'block',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  ...elementStyles.copyright,
                  cursor: 'context-menu',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onContextMenu={(e) => handleContextMenu(e, 'copyright')}
              >
            © {currentYear} {exampleCompany.name}. {t('ecommerceFooterSettings.preview.allRightsReserved')}
              </Typography>
              {isCorporateLayout && footerSettings.legalEntityName.trim() && (
                <Typography variant="body2" sx={{ ...elementStyles.copyright, opacity: 0.85 }}>
                  {footerSettings.legalEntityName.trim()}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        
        <SectionTitle>
          <EditIcon /> {t('ecommerceFooterSettings.sections.companyDescriptionManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.helpers.companyDescription')}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            label={t('ecommerceFooterSettings.fields.companyDescription')}
            value={tempDescription}
            onChange={(e) => applyCompanyDescriptionText(e.target.value, false)}
            onBlur={(e) => applyCompanyDescriptionText(e.target.value, true)}
            placeholder={t('ecommerceFooterSettings.modals.descriptionPlaceholder')}
            helperText={t('ecommerceFooterSettings.modals.charCount', { count: tempDescription.length, max: 500 })}
            inputProps={{ maxLength: 500 }}
          />
        </Paper>

        {/* Logo Yönetimi */}
        <SectionTitle>
          <ImageIcon /> {t('ecommerceFooterSettings.sections.companyLogoManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Şirket logosunu yükleyerek footer'da şirket adı yerine logo gösterebilirsiniz.
          </Typography>
          
          {/* Mevcut Logo */}
          {footerSettings.companyLogo && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Mevcut Logo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}>
                <img
                  src={footerSettings.companyLogo}
                  alt="Company Logo"
                  style={{
                    width: `${footerSettings.logoWidth}px`,
                    height: `${footerSettings.logoHeight}px`,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Logo boyutu: {footerSettings.logoWidth}x{footerSettings.logoHeight}px
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveLogo}
                  >
                    Logoyu Sil
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* ✅ YENİ: Resim Kütüphanesinden Logo Seç */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {footerSettings.companyLogo ? t('ecommerceFooterSettings.actions.changeLogo') : t('ecommerceFooterSettings.actions.selectLogo')}
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleOpenImageLibrary('logo1')}
            >
              Resim Kütüphanesinden Seç
            </Button>
          </Box>

          {/* Logo Boyut Ayarları */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Logo Boyut Ayarları
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {/* Genişlik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Genişlik: {footerSettings.logoWidth}px
                </Typography>
                <Slider
                  value={footerSettings.logoWidth}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logoWidth: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logoWidth: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo-width-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={50}
                  max={300}
                />
              </Box>
              {/* Yükseklik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Yükseklik: {footerSettings.logoHeight}px
                </Typography>
                <Slider
                  value={footerSettings.logoHeight}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logoHeight: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logoHeight: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo-height-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={30}
                  max={150}
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* İkinci Logo Yönetimi */}
        <SectionTitle>
          <ImageIcon /> {t('ecommerceFooterSettings.sections.secondCompanyLogoManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            İkinci şirket logosunu yükleyerek footer'da ana logonun altında gösterebilirsiniz.
          </Typography>
          
          {/* Mevcut İkinci Logo */}
          {footerSettings.companyLogo2 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Mevcut İkinci Logo
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                p: 2, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1,
                bgcolor: 'grey.50'
              }}>
                <img
                  src={footerSettings.companyLogo2}
                  alt="Second Company Logo"
                  style={{
                    width: `${footerSettings.logo2Width}px`,
                    height: `${footerSettings.logo2Height}px`,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Logo boyutu: {footerSettings.logo2Width}x{footerSettings.logo2Height}px
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveLogo2}
                  >
                    İkinci Logoyu Sil
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* ✅ YENİ: Resim Kütüphanesinden İkinci Logo Seç */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {footerSettings.companyLogo2
                ? t('ecommerceFooterSettings.actions.changeLogo')
                : t('ecommerceFooterSettings.actions.selectLogo')}
            </Typography>
            
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleOpenImageLibrary('logo2')}
            >
              Resim Kütüphanesinden Seç
            </Button>
          </Box>

          {/* İkinci Logo Boyut Ayarları */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              İkinci Logo Boyut Ayarları
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {/* Genişlik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Genişlik: {footerSettings.logo2Width}px
                </Typography>
                <Slider
                  value={footerSettings.logo2Width}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logo2Width: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logo2Width: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo2-width-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={50}
                  max={300}
                />
              </Box>
              {/* Yükseklik */}
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Yükseklik: {footerSettings.logo2Height}px
                </Typography>
                <Slider
                  value={footerSettings.logo2Height}
                  onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, logo2Height: newValue as number }))}
                  onChangeCommitted={(e, newValue) => {
                    const updatedSettings = { ...footerSettings, logo2Height: newValue as number };
                    setFooterSettings(updatedSettings);
                    autoSaveFooterSettings(updatedSettings);
                  }}
                  aria-labelledby="logo2-height-slider"
                  valueLabelDisplay="auto"
                  step={5}
                  marks
                  min={30}
                  max={150}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
        
        {/* İletişim Bilgileri — İletişim sayfasından otomatik */}
        <SectionTitle>
          <SettingsIcon /> {t('ecommerceFooterSettings.sections.contactInformationManagement')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.helpers.contactAutoIcons')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2">{t('ecommerceFooterSettings.fields.showContactIcons')}:</Typography>
            <Box
              component="input"
              type="checkbox"
              checked={footerSettings.showContactIcons}
              onChange={(e: any) => {
                const updated = { ...footerSettings, showContactIcons: e.target.checked };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              sx={{ width: 18, height: 18, cursor: 'pointer' }}
            />
          </Box>
          {contactPreview.address || contactPreview.phone || contactPreview.email || contactPreview.workingHours ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {contactPreview.address && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PlaceOutlinedIcon sx={{ fontSize: 18, mt: 0.2, color: 'text.secondary' }} />
                  {contactPreview.address}
                </Typography>
              )}
              {contactPreview.phone && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneAndroidIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  {contactPreview.phone}
                </Typography>
              )}
              {contactPreview.email && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MailOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  {contactPreview.email}
                </Typography>
              )}
              {contactPreview.workingHours && (
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, whiteSpace: 'pre-line' }}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: 18, mt: 0.2, color: 'text.secondary' }} />
                  {contactPreview.workingHours}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('ecommerceFooterSettings.helpers.contactEmpty')}
            </Typography>
          )}
          {onNavigate && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={() => onNavigate('contact')}
            >
              {t('ecommerceFooterSettings.actions.editInContactPage')}
            </Button>
          )}
        </Paper>

        {/* Sosyal Medya Yönetimi */}
        <SectionTitle>{t('ecommerceFooterSettings.sections.socialMediaManagement')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.socialMedia.description')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ minWidth: '100px' }}>
              {t('ecommerceFooterSettings.socialMedia.iconSize')}:
            </Typography>
            <Slider
              value={footerSettings.socialIconSize}
              onChange={(e, newValue) => setFooterSettings(prev => ({ ...prev, socialIconSize: newValue as number }))}
              onChangeCommitted={(e, newValue) => {
                const updatedSettings = { ...footerSettings, socialIconSize: newValue as number };
                setFooterSettings(updatedSettings);
                autoSaveFooterSettings(updatedSettings);
              }}
              aria-labelledby="social-icon-size-slider"
              valueLabelDisplay="auto"
              step={2}
              marks
              min={16}
              max={64}
              sx={{ maxWidth: 320 }}
            />
            <Typography sx={{ minWidth: '40px' }}>{footerSettings.socialIconSize}px</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">{t('ecommerceFooterSettings.socialMedia.layout')}:</Typography>
            <Button
              size="small"
              variant={footerSettings.iconLayout === 'row' ? 'contained' : 'outlined'}
              startIcon={<ViewStreamIcon />}
              onClick={() => {
                const updated = { ...footerSettings, iconLayout: 'row' as FooterIconLayout };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
            >
              {t('ecommerceFooterSettings.socialMedia.layoutRow')}
            </Button>
            <Button
              size="small"
              variant={footerSettings.iconLayout === 'grid' ? 'contained' : 'outlined'}
              startIcon={<ViewModuleIcon />}
              onClick={() => {
                const updated = { ...footerSettings, iconLayout: 'grid' as FooterIconLayout };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
            >
              {t('ecommerceFooterSettings.socialMedia.layoutGrid')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {t('ecommerceFooterSettings.socialMedia.layoutHint')}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <List dense>
            {footerSettings.socialLinks.length > 0 ? footerSettings.socialLinks.map((link) => (
              <ListItem
                key={link.id}
                secondaryAction={
                  <>
                    <IconButton edge="end" aria-label="edit" sx={{ mr: 1 }} onClick={() => handleOpenSocialModal(link)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" aria-label="delete" onClick={() => openDeleteConfirm(link.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
              >
                <ListItemIcon sx={{ minWidth: footerSettings.socialIconSize + 16 }}>
                  <SocialIcon platform={link.platform} icon={link.icon} size={footerSettings.socialIconSize} />
                </ListItemIcon>
                <ListItemText
                  primary={getSocialPlatformLabel(link.platform)}
                  secondary={link.url}
                />
              </ListItem>
            )) : (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                {t('ecommerceFooterSettings.socialMedia.noLinks')}
              </Typography>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => handleOpenSocialModal()}
          >
            {t('ecommerceFooterSettings.actions.addSocialLink')}
          </Button>
        </Paper>

        <SectionTitle>{t('ecommerceFooterSettings.sections.columnOrder')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.columnOrder.description')}
          </Typography>
          {([
            { side: 'left' as const, slots: columnsBySide.left },
            { side: 'right' as const, slots: columnsBySide.right },
          ]).map((group) => (
            <Box key={group.side} sx={{ mb: group.side === 'left' ? 2 : 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {group.side === 'left'
                  ? t('ecommerceFooterSettings.columnOrder.sideLeft')
                  : t('ecommerceFooterSettings.columnOrder.sideRight')}
              </Typography>
              {group.slots.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.columnOrder.sideEmpty')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {group.slots.map((slot, index) => (
                    <Box
                      key={slot.type === 'quickAccess' ? `order-${slot.id}` : `order-${slot.type}`}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 1,
                        py: 0.75,
                        bgcolor: 'grey.50',
                        minWidth: 160,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => handleMoveColumn(slot, -1)}
                          aria-label={t('ecommerceFooterSettings.columnOrder.moveLeft')}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{ flex: 1, textAlign: 'center', fontWeight: 600 }}>
                          {getColumnOrderLabel(slot)}
                        </Typography>
                        <IconButton
                          size="small"
                          disabled={index === group.slots.length - 1}
                          onClick={() => handleMoveColumn(slot, 1)}
                          aria-label={t('ecommerceFooterSettings.columnOrder.moveRight')}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        {([
                          { value: 'left' as const, label: t('ecommerceFooterSettings.columnOrder.sideLeftShort') },
                          { value: 'right' as const, label: t('ecommerceFooterSettings.columnOrder.sideRightShort') },
                        ]).map((option) => (
                          <Button
                            key={option.value}
                            size="small"
                            variant={resolveFooterColumnSide(slot.side) === option.value ? 'contained' : 'outlined'}
                            onClick={() => handleColumnSideChange(slot, option.value)}
                            sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 12 }}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Paper>

        {/* Hızlı Erişim Yönetimi */}
        <SectionTitle>{t('ecommerceFooterSettings.sections.quickAccessManagement')}</SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: getQuickAccessGridTemplate(footerSettings.quickAccessColumns.length), gap: 2 }}>
            {footerSettings.quickAccessColumns.map((column, columnIndex) => {
              const columnLinks = footerSettings.quickLinks.filter(l => l.column === column.id);
              return (
                <Box key={column.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label={t('ecommerceFooterSettings.quickAccess.columnTitleLabel')}
                      placeholder={t('ecommerceFooterSettings.quickAccess.columnTitlePlaceholder', { number: columnIndex + 1 })}
                      value={column.title ?? ''}
                      onChange={(e) => handleUpdateColumnTitle(column.id, e.target.value)}
                      onBlur={(e) => handleColumnTitleBlur(column.id, e.target.value)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {(() => {
                        const slot = resolvedColumnOrder.find(
                          (item) => item.type === 'quickAccess' && item.id === column.id
                        );
                        if (!slot) return null;
                        const sideSlots = resolveFooterColumnSide(slot.side) === 'right'
                          ? columnsBySide.right
                          : columnsBySide.left;
                        const sideIndex = sideSlots.findIndex(
                          (item) => getFooterColumnKey(item) === getFooterColumnKey(slot)
                        );
                        return (
                          <>
                            <IconButton
                              size="small"
                              disabled={sideIndex <= 0}
                              onClick={() => handleMoveColumn(slot, -1)}
                              aria-label={t('ecommerceFooterSettings.columnOrder.moveLeft')}
                            >
                              <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={sideIndex < 0 || sideIndex >= sideSlots.length - 1}
                              onClick={() => handleMoveColumn(slot, 1)}
                              aria-label={t('ecommerceFooterSettings.columnOrder.moveRight')}
                            >
                              <ChevronRightIcon fontSize="small" />
                            </IconButton>
                          </>
                        );
                      })()}
                      {footerSettings.quickAccessColumns.length > 1 && !column.hasFixedLinks && (
                        <IconButton
                          size="small"
                          aria-label="delete column"
                          onClick={() => openColumnDeleteConfirm(column.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {column.hasFixedLinks
                      ? t('ecommerceFooterSettings.quickAccess.column1Hint')
                      : t('ecommerceFooterSettings.quickAccess.columnHint', { count: column.maxLinks })}
                  </Typography>
                  <List dense>
                    {column.hasFixedLinks && (
                      <>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.quickAccess.home')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.preview.aboutUs')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                        <ListItem dense sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 0.5 }}>
                          <ListItemText primary={t('ecommerceFooterSettings.quickAccess.contact')} secondary={t('ecommerceFooterSettings.quickAccess.fixed')} />
                        </ListItem>
                      </>
                    )}
                    {columnLinks.length > 0 ? columnLinks.map(link => (
                      <ListItem
                        key={link.id}
                        secondaryAction={
                          <>
                            <IconButton size="small" edge="end" aria-label="edit" sx={{ mr: 0.5 }} onClick={() => handleOpenQuickLinkModal(link)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" edge="end" aria-label="delete" onClick={() => openQuickLinkDeleteConfirm(link.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        }
                      >
                        <ListItemText primary={link.label} secondary={formatFooterLinkSecondary(link)} />
                      </ListItem>
                    )) : !column.hasFixedLinks && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        {t('ecommerceFooterSettings.quickAccess.noLinksInColumn')}
                      </Typography>
                    )}
                  </List>
                  <Button
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => handleOpenQuickLinkModal(null, column.id)}
                    sx={{ mt: 1 }}
                  >
                    {t('ecommerceFooterSettings.actions.addQuickAccessLink')}
                  </Button>
                </Box>
              );
            })}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddQuickAccessColumn}
            disabled={footerSettings.quickAccessColumns.length >= MAX_QUICK_ACCESS_COLUMNS}
          >
            {t('ecommerceFooterSettings.actions.addQuickAccessColumn')}
          </Button>
        </Paper>

        {/* Yasal Belgeler Kolonu */}
        <SectionTitle>
          Yasal Belgeler
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aktif yasal belgeler (Çerez Politikası, KVKK, Gizlilik, Satış Sözleşmesi, İptal-İade, Kullanım Koşulları)
            footer'da otomatik olarak listelenir. Belgeleri "Yasal Sözleşmeler" sekmesinden yönetebilirsiniz.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Yasal belgeleri footer'da otomatik göster:</Typography>
            <Box
              component="input"
              type="checkbox"
              checked={footerSettings.showLegalColumn}
              onChange={(e: any) => {
                const updated = { ...footerSettings, showLegalColumn: e.target.checked };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              sx={{ width: 20, height: 20, cursor: 'pointer' }}
            />
            <Typography variant="body2" color="text.secondary">
              {footerSettings.showLegalColumn ? 'Açık' : 'Kapalı'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {footerSettings.showLegalColumn && (
              <TextField
                label="Kolon Başlığı"
                value={footerSettings.legalColumnTitle}
                onChange={(e) => {
                  const updated = { ...footerSettings, legalColumnTitle: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                size="small"
                sx={{ maxWidth: 320, minWidth: 200 }}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<GavelOutlined />}
              endIcon={<OpenInNew fontSize="small" />}
              onClick={() => onNavigate?.('legal')}
              disabled={!onNavigate}
            >
              Yasal Sözleşmeler
            </Button>
          </Box>
        </Paper>

        {/* Ödeme logoları — marka sütunu, slogan altı */}
        <SectionTitle>
          <CreditCardIcon /> {t('ecommerceFooterSettings.sections.paymentLogos')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.paymentLogos.description')}
          </Typography>

          <Box
            onClick={() => {
              if (paymentUploading || footerSettings.paymentLogosImages.length >= 8) return;
              handleOpenImageLibrary('paymentLogos');
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (footerSettings.paymentLogosImages.length < 8) setPaymentDropActive(true);
            }}
            onDragLeave={() => setPaymentDropActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setPaymentDropActive(false);
              if (e.dataTransfer.files?.length) {
                void handlePaymentFilesUpload(e.dataTransfer.files);
              }
            }}
            sx={{
              border: '2px dashed',
              borderColor: paymentDropActive ? 'primary.main' : 'divider',
              bgcolor: paymentDropActive ? 'action.hover' : 'grey.50',
              borderRadius: 2,
              minHeight: { xs: 140, sm: 160 },
              p: { xs: 1.5, sm: 2 },
              mb: 2,
              cursor: footerSettings.paymentLogosImages.length >= 8 ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <input
              ref={paymentFileInputRef}
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={(e) => {
                if (e.target.files?.length) {
                  void handlePaymentFilesUpload(e.target.files);
                }
                e.target.value = '';
              }}
            />

            {footerSettings.paymentLogosImages.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                {footerSettings.paymentLogosImages.map((src) => (
                  <Box
                    key={src}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      position: 'relative',
                      p: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 72,
                    }}
                  >
                    <img
                      src={src}
                      alt=""
                      style={{
                        width: '100%',
                        maxWidth: 160,
                        height: 40,
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemovePaymentLogo(src)}
                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'background.paper' }}
                      aria-label={t('ecommerceFooterSettings.actions.deleteImage')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 2,
                px: 1,
              }}>
                {paymentUploading ? (
                  <CircularProgress size={28} />
                ) : (
                  <CloudUploadIcon color="action" sx={{ fontSize: 36, mb: 1 }} />
                )}
                <Typography variant="subtitle2">
                  {t('ecommerceFooterSettings.paymentLogos.dropTitle')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.paymentLogos.dropHint')}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={paymentUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              disabled={paymentUploading || footerSettings.paymentLogosImages.length >= 8}
              onClick={(e) => {
                e.stopPropagation();
                paymentFileInputRef.current?.click();
              }}
            >
              {t('ecommerceFooterSettings.paymentLogos.uploadFiles')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={() => handleOpenImageLibrary('paymentLogos')}
              disabled={footerSettings.paymentLogosImages.length >= 8}
            >
              {footerSettings.paymentLogosImages.length > 0
                ? t('ecommerceFooterSettings.actions.addPaymentLogo')
                : t('ecommerceFooterSettings.actions.selectPaymentLogos')}
            </Button>
          </Box>

          {footerSettings.paymentLogosImages.length > 0 && (
            <Box sx={{ maxWidth: 480 }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  {t('ecommerceFooterSettings.paymentLogos.align')}
                </FormLabel>
                <RadioGroup
                  row
                  value={footerSettings.paymentLogosAlign}
                  onChange={(e) => {
                    const updated = {
                      ...footerSettings,
                      paymentLogosAlign: e.target.value as 'left' | 'center' | 'right'
                    };
                    setFooterSettings(updated);
                    autoSaveFooterSettings(updated);
                  }}
                >
                  <FormControlLabel value="left" control={<Radio size="small" />} label={t('ecommerceFooterSettings.paymentLogos.alignLeft')} />
                  <FormControlLabel value="center" control={<Radio size="small" />} label={t('ecommerceFooterSettings.paymentLogos.alignCenter')} />
                  <FormControlLabel value="right" control={<Radio size="small" />} label={t('ecommerceFooterSettings.paymentLogos.alignRight')} />
                </RadioGroup>
              </FormControl>

              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('ecommerceFooterSettings.paymentLogos.width')}: {footerSettings.paymentLogosWidth}px
              </Typography>
              <Slider
                value={footerSettings.paymentLogosWidth}
                min={80}
                max={600}
                step={10}
                onChange={(_e, newValue) => setFooterSettings(prev => ({ ...prev, paymentLogosWidth: newValue as number }))}
                onChangeCommitted={(_e, newValue) => {
                  const updatedSettings = { ...footerSettings, paymentLogosWidth: newValue as number };
                  setFooterSettings(updatedSettings);
                  autoSaveFooterSettings(updatedSettings);
                }}
                sx={{ mb: 2 }}
              />

              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('ecommerceFooterSettings.paymentLogos.height')}: {footerSettings.paymentLogosHeight}px
              </Typography>
              <Slider
                value={footerSettings.paymentLogosHeight}
                min={24}
                max={160}
                step={2}
                onChange={(_e, newValue) => setFooterSettings(prev => ({ ...prev, paymentLogosHeight: newValue as number }))}
                onChangeCommitted={(_e, newValue) => {
                  const updatedSettings = { ...footerSettings, paymentLogosHeight: newValue as number };
                  setFooterSettings(updatedSettings);
                  autoSaveFooterSettings(updatedSettings);
                }}
              />
            </Box>
          )}
        </Paper>

        {/* Bülten / Newsletter Bölümü */}
        <SectionTitle>
          <MailOutlineIcon /> {t('ecommerceFooterSettings.sections.newsletter')}
        </SectionTitle>
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.newsletter.descriptionText')}
          </Typography>

          {/* Açma/Kapama */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{t('ecommerceFooterSettings.newsletter.showSection')}:</Typography>
            <Box
              component="input"
              type="checkbox"
              checked={footerSettings.showNewsletterSection}
              onChange={(e: any) => {
                const updated = { ...footerSettings, showNewsletterSection: e.target.checked };
                setFooterSettings(updated);
                autoSaveFooterSettings(updated);
              }}
              sx={{ width: 20, height: 20, cursor: 'pointer' }}
            />
            <Typography variant="body2" color="text.secondary">
              {footerSettings.showNewsletterSection ? t('ecommerceFooterSettings.newsletter.active') : t('ecommerceFooterSettings.newsletter.passive')}
            </Typography>
          </Box>

          {footerSettings.showNewsletterSection && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Başlık */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.titleLabel')}
                value={footerSettings.newsletterTitle}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterTitle: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.titlePlaceholder')}
                helperText={t('ecommerceFooterSettings.newsletter.titleHelper')}
              />

              {/* Açıklama */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.descriptionLabel')}
                value={footerSettings.newsletterDescription}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterDescription: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.descriptionPlaceholder')}
                helperText={t('ecommerceFooterSettings.newsletter.descriptionHelper')}
              />

              {/* Buton Metni */}
              <TextField
                fullWidth
                label={t('ecommerceFooterSettings.newsletter.buttonTextLabel')}
                value={footerSettings.newsletterButtonText}
                onChange={(e) => {
                  const updated = { ...footerSettings, newsletterButtonText: e.target.value };
                  setFooterSettings(updated);
                }}
                onBlur={() => autoSaveFooterSettings()}
                placeholder={t('ecommerceFooterSettings.newsletter.subscribe')}
                helperText={t('ecommerceFooterSettings.newsletter.buttonTextHelper')}
              />

              {/* Cinsiyet Seçimi */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.showGenderSelection')}:</Typography>
                <Box
                  component="input"
                  type="checkbox"
                  checked={footerSettings.showGenderSelection}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, showGenderSelection: e.target.checked };
                    setFooterSettings(updated);
                    autoSaveFooterSettings(updated);
                  }}
                  sx={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.newsletter.genderSelectionHint')}
                </Typography>
              </Box>

              {/* Arka Plan Rengi */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ minWidth: '120px' }}>{t('ecommerceFooterSettings.newsletter.backgroundColor')}:</Typography>
                <Box
                  component="input"
                  type="color"
                  value={footerSettings.newsletterBgColor}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterBgColor: e.target.value };
                    setFooterSettings(updated);
                  }}
                  onBlur={() => autoSaveFooterSettings()}
                  sx={{ width: '50px', height: '30px', border: '1px solid #ccc', borderRadius: 1, cursor: 'pointer' }}
                />
                <Box
                  component="input"
                  type="text"
                  value={footerSettings.newsletterBgColor}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterBgColor: e.target.value };
                    setFooterSettings(updated);
                  }}
                  onBlur={() => autoSaveFooterSettings()}
                  sx={{ width: '120px', p: 1, border: '1px solid #ccc', borderRadius: 1, fontSize: '14px' }}
                />
              </Box>

              {/* Masaüstü Görsel Seçimi */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{t('ecommerceFooterSettings.newsletter.desktopImage')}</Typography>
              {footerSettings.newsletterImage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <img
                    src={footerSettings.newsletterImage}
                    alt="Newsletter image"
                    style={{
                      width: `${Math.min(footerSettings.newsletterImageWidth, 200)}px`,
                      height: `${Math.min(footerSettings.newsletterImageHeight, 150)}px`,
                      objectFit: 'cover',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('ecommerceFooterSettings.newsletter.size')}: {footerSettings.newsletterImageWidth}x{footerSettings.newsletterImageHeight}px
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={async () => {
                        const updated = { ...footerSettings, newsletterImage: '' };
                        setFooterSettings(updated);
                        await saveFooterSettings(updated);
                        setSnackbar({ open: true, message: t('ecommerceFooterSettings.messages.newsletterImageDeleted'), severity: 'success' });
                      }}
                    >
                      {t('ecommerceFooterSettings.actions.deleteImage')}
                    </Button>
                  </Box>
                </Box>
              )}
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => handleOpenImageLibrary('newsletter')}
              >
                {footerSettings.newsletterImage ? t('ecommerceFooterSettings.actions.changeImage') : t('ecommerceFooterSettings.actions.selectImage')}
              </Button>

              {/* Masaüstü Görsel Boyut Ayarları */}
              {footerSettings.newsletterImage && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.newsletter.width')}: {footerSettings.newsletterImageWidth}px</Typography>
                    <Slider
                      value={footerSettings.newsletterImageWidth}
                      onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterImageWidth: v as number }))}
                      onChangeCommitted={(_e, v) => {
                        const updated = { ...footerSettings, newsletterImageWidth: v as number };
                        setFooterSettings(updated);
                        autoSaveFooterSettings(updated);
                      }}
                      min={100} max={800} step={10}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.newsletter.height')}: {footerSettings.newsletterImageHeight}px</Typography>
                    <Slider
                      value={footerSettings.newsletterImageHeight}
                      onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterImageHeight: v as number }))}
                      onChangeCommitted={(_e, v) => {
                        const updated = { ...footerSettings, newsletterImageHeight: v as number };
                        setFooterSettings(updated);
                        autoSaveFooterSettings(updated);
                      }}
                      min={100} max={600} step={10}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Box>
              )}

              {/* Mobil Ayarlar */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneAndroidIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                {t('ecommerceFooterSettings.newsletter.mobileSettings')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                {t('ecommerceFooterSettings.newsletter.mobileSettingsHint')}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.showImageOnMobile')}:</Typography>
                <Box
                  component="input"
                  type="checkbox"
                  checked={footerSettings.newsletterShowImageOnMobile}
                  onChange={(e: any) => {
                    const updated = { ...footerSettings, newsletterShowImageOnMobile: e.target.checked };
                    setFooterSettings(updated);
                    autoSaveFooterSettings(updated);
                  }}
                  sx={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceFooterSettings.newsletter.showImageOnMobileHint')}
                </Typography>
              </Box>

              {footerSettings.newsletterShowImageOnMobile && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                    {t('ecommerceFooterSettings.newsletter.mobileImage')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('ecommerceFooterSettings.newsletter.mobileImageHint')}
                  </Typography>
                  {footerSettings.newsletterMobileImage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                      <img
                        src={footerSettings.newsletterMobileImage}
                        alt="Newsletter mobile"
                        style={{
                          width: `${Math.min(footerSettings.newsletterMobileImageWidth, 160)}px`,
                          height: `${Math.min(footerSettings.newsletterMobileImageHeight, 120)}px`,
                          objectFit: 'cover',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {t('ecommerceFooterSettings.newsletter.size')}: {footerSettings.newsletterMobileImageWidth}x{footerSettings.newsletterMobileImageHeight}px
                        </Typography>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={async () => {
                            const updated = { ...footerSettings, newsletterMobileImage: '' };
                            setFooterSettings(updated);
                            await saveFooterSettings(updated);
                            setSnackbar({ open: true, message: t('ecommerceFooterSettings.messages.newsletterMobileImageDeleted'), severity: 'success' });
                          }}
                        >
                          {t('ecommerceFooterSettings.actions.deleteImage')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<PhoneAndroidIcon />}
                    onClick={() => handleOpenImageLibrary('newsletterMobile')}
                  >
                    {footerSettings.newsletterMobileImage
                      ? t('ecommerceFooterSettings.actions.changeMobileImage')
                      : t('ecommerceFooterSettings.actions.selectMobileImage')}
                  </Button>

                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('ecommerceFooterSettings.newsletter.mobileWidth')}: {footerSettings.newsletterMobileImageWidth}px
                      </Typography>
                      <Slider
                        value={footerSettings.newsletterMobileImageWidth}
                        onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterMobileImageWidth: v as number }))}
                        onChangeCommitted={(_e, v) => {
                          const updated = { ...footerSettings, newsletterMobileImageWidth: v as number };
                          setFooterSettings(updated);
                          autoSaveFooterSettings(updated);
                        }}
                        min={80} max={400} step={10}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('ecommerceFooterSettings.newsletter.mobileHeight')}: {footerSettings.newsletterMobileImageHeight}px
                      </Typography>
                      <Slider
                        value={footerSettings.newsletterMobileImageHeight}
                        onChange={(_e, v) => setFooterSettings(prev => ({ ...prev, newsletterMobileImageHeight: v as number }))}
                        onChangeCommitted={(_e, v) => {
                          const updated = { ...footerSettings, newsletterMobileImageHeight: v as number };
                          setFooterSettings(updated);
                          autoSaveFooterSettings(updated);
                        }}
                        min={80} max={400} step={10}
                        valueLabelDisplay="auto"
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* Önizleme */}
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>{t('ecommerceFooterSettings.preview.title')}</Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 4,
                p: 4,
                bgcolor: footerSettings.newsletterBgColor,
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <Box sx={{ flex: 1, minWidth: 280 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, fontStyle: 'italic' }}>
                    {footerSettings.newsletterTitle || t('ecommerceFooterSettings.newsletter.titleNotProvided')}
                  </Typography>
                  {footerSettings.newsletterDescription && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {footerSettings.newsletterDescription}
                    </Typography>
                  )}
                  {footerSettings.showGenderSelection && (
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="input" type="radio" name="preview-gender" defaultChecked sx={{ cursor: 'pointer' }} />
                        <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.female')}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="input" type="radio" name="preview-gender" sx={{ cursor: 'pointer' }} />
                        <Typography variant="body2">{t('ecommerceFooterSettings.newsletter.male')}</Typography>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      component="input"
                      type="email"
                      placeholder={t('ecommerceFooterSettings.newsletter.emailPlaceholder')}
                      disabled
                      sx={{ flex: 1, p: 1.5, border: '1px solid #ccc', borderRadius: 0, fontSize: '14px', bgcolor: '#fff' }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', letterSpacing: 2, fontWeight: 500 }}>
                    {footerSettings.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe')}
                  </Typography>
                </Box>
                {footerSettings.newsletterImage && (
                  <Box sx={{ flexShrink: 0, marginRight: `${footerSettings.newsletterImageSpacing}px` }}>
                    <img
                      src={footerSettings.newsletterImage}
                      alt="Newsletter"
                      style={{
                        width: `${Math.min(footerSettings.newsletterImageWidth, 250)}px`,
                        height: `${Math.min(footerSettings.newsletterImageHeight, 200)}px`,
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                )}
              </Box>

              {/* Mobil Önizleme */}
              {footerSettings.newsletterShowImageOnMobile && (
                <Box sx={{
                  mt: 2,
                  maxWidth: 360,
                  mx: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: 3,
                  bgcolor: footerSettings.newsletterBgColor,
                  borderRadius: 1,
                  border: '1px dashed #90caf9',
                  alignItems: 'stretch',
                }}>
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <PhoneAndroidIcon sx={{ fontSize: 14 }} />
                    {t('ecommerceFooterSettings.newsletter.mobilePreview')}
                  </Typography>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, fontStyle: 'italic', fontSize: '1rem' }}>
                      {footerSettings.newsletterTitle || t('ecommerceFooterSettings.newsletter.titleNotProvided')}
                    </Typography>
                    {footerSettings.newsletterDescription && (
                      <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary', display: 'block' }}>
                        {footerSettings.newsletterDescription}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ letterSpacing: 1, fontWeight: 500 }}>
                      {footerSettings.newsletterButtonText || t('ecommerceFooterSettings.newsletter.subscribe')}
                    </Typography>
                  </Box>
                  {(footerSettings.newsletterMobileImage || footerSettings.newsletterImage) && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={footerSettings.newsletterMobileImage || footerSettings.newsletterImage}
                        alt="Newsletter mobile preview"
                        style={{
                          width: `${Math.min(footerSettings.newsletterMobileImageWidth, 280)}px`,
                          maxWidth: '100%',
                          height: `${Math.min(footerSettings.newsletterMobileImageHeight, 200)}px`,
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Paper>

      </Box>

      {/* Sağ tık menüsü */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditClick}>{t('ecommerceFooterSettings.modals.edit')}</MenuItem>
      </Menu>

      {/* Stil düzenleme modalı */}
      {editingElementKey && editingElementKey !== 'backgroundColor' && (
        <ElementStyleEditorModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          elementKey={editingElementKey}
          currentStyles={elementStyles[editingElementKey]}
          onSave={handleStyleSave}
        />
      )}

      {/* Arka plan rengi düzenleme modalı */}
      <Dialog 
        open={isBackgroundModalOpen} 
        onClose={() => setIsBackgroundModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.footerBackgroundColorTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerceFooterSettings.modals.footerBackgroundColorDescription')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="input"
              type="color"
              value={tempBackgroundColor}
              onChange={(e: any) => setTempBackgroundColor(e.target.value)}
              sx={{ 
                width: '80px', 
                height: '40px', 
                border: 'none', 
                borderRadius: 1, 
                cursor: 'pointer' 
              }}
            />
            <Box
              component="input"
              type="text"
              value={tempBackgroundColor}
              onChange={(e: any) => setTempBackgroundColor(e.target.value)}
              placeholder="#1f2937"
              sx={{ 
                flexGrow: 1, 
                p: 1, 
                border: '1px solid #ccc', 
                borderRadius: 1 
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBackgroundModalOpen(false)}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button 
            onClick={handleBackgroundColorSave} 
            variant="contained"
          >
            {t('ecommerceFooterSettings.modals.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şirket açıklaması düzenleme modalı */}
      <Dialog 
        open={isDescriptionModalOpen} 
        onClose={() => setIsDescriptionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.editCompanyDescriptionTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('ecommerceFooterSettings.modals.editCompanyDescriptionDescription')}
          </Typography>
          
          {/* Metin Düzenleme Alanı */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('ecommerceFooterSettings.modals.descriptionText')}</Typography>
            <Box
              component="textarea"
              value={tempDescription}
              onChange={(e: any) => {
                if (e.target.value.length <= 500) {
                  setTempDescription(e.target.value);
                }
              }}
              placeholder={t('ecommerceFooterSettings.modals.descriptionPlaceholder')}
              sx={{ 
                width: '100%',
                minHeight: '100px',
                p: 2, 
                border: '1px solid #ccc', 
                borderRadius: 1,
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceFooterSettings.modals.charCount', { count: tempDescription.length, max: 500 })}
            </Typography>
          </Box>

          {/* Stil Düzenleme */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>{t('ecommerceFooterSettings.modals.textStyle')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Font Boyutu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '80px' }}>{t('ecommerceFooterSettings.modals.size')}</Typography>
              <Box
                component="input"
                type="range"
                min="10"
                max="24"
                step="1"
                value={parseInt(tempDescriptionStyles.fontSize.replace('rem', '').replace('px', ''))}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  fontSize: e.target.value + 'px' 
                }))}
                sx={{ flexGrow: 1 }}
              />
              <Typography sx={{ minWidth: '40px' }}>
                {tempDescriptionStyles.fontSize}
              </Typography>
            </Box>

            {/* Renk */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ minWidth: '80px' }}>{t('ecommerceFooterSettings.modals.color')}</Typography>
              <Box
                component="input"
                type="color"
                value={tempDescriptionStyles.color}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  color: e.target.value 
                }))}
                sx={{ 
                  width: '50px', 
                  height: '30px', 
                  border: 'none', 
                  borderRadius: 1, 
                  cursor: 'pointer' 
                }}
              />
              <Box
                component="input"
                type="text"
                value={tempDescriptionStyles.color}
                onChange={(e: any) => setTempDescriptionStyles(prev => ({ 
                  ...prev, 
                  color: e.target.value 
                }))}
                sx={{ 
                  flexGrow: 1, 
                  p: 1, 
                  border: '1px solid #ccc', 
                  borderRadius: 1 
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDescriptionModalOpen(false)}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button 
            onClick={handleDescriptionSave} 
            variant="contained"
          >
            {t('ecommerceFooterSettings.modals.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Sosyal Medya Silme Onay Modalı */}
      <Dialog
        open={isDeleteConfirmOpen}
        onClose={closeDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('ecommerceFooterSettings.modals.deleteConfirmationTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('ecommerceFooterSettings.modals.deleteSocialLinkConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteSocialLink} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hızlı Erişim Sütun Silme Onay Modalı */}
      <Dialog
        open={isColumnDeleteConfirmOpen}
        onClose={closeColumnDeleteConfirm}
      >
        <DialogTitle>{t('ecommerceFooterSettings.modals.deleteColumnTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ecommerceFooterSettings.modals.deleteColumnDescription')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeColumnDeleteConfirm}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
          <Button onClick={handleDeleteQuickAccessColumn} color="error" autoFocus>
            {t('ecommerceFooterSettings.modals.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hızlı Erişim Linki Silme Onay Modalı */}
      <Dialog
        open={isQuickLinkDeleteConfirmOpen}
        onClose={closeQuickLinkDeleteConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Confirmation"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to permanently delete this quick access link? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuickLinkDeleteConfirm}>Cancel</Button>
          <Button onClick={handleDeleteQuickLink} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sosyal Medya Ekleme/Düzenleme Modalı */}
      <SocialMediaModal
        open={isSocialModalOpen}
        onClose={handleCloseSocialModal}
        onSave={handleSaveSocialLink}
        existingLink={editingSocialLink}
      />

      {/* Hızlı Erişim Linki Ekleme/Düzenleme Modalı */}
      <QuickAccessLinkModal
        open={isQuickLinkModalOpen}
        onClose={handleCloseQuickLinkModal}
        onSave={handleSaveQuickLink}
        existingLink={editingQuickLink}
        columns={footerSettings.quickAccessColumns}
        defaultColumn={quickLinkDefaultColumn}
        getColumnTitle={getColumnTitle}
        configId={configId}
      />

      {/* ✅ YENİ: Resim Kütüphanesi Modalı */}
      <ImageLibrarySelectionModal
        open={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        onSave={handleImageSelect}
        companyId={companyId}
        productName={
          currentLogoType === 'newsletter'
            ? t('ecommerceFooterSettings.imageNames.newsletterImage')
            : currentLogoType === 'newsletterMobile'
              ? t('ecommerceFooterSettings.imageNames.newsletterMobileImage')
              : currentLogoType === 'logo1'
                ? t('ecommerceFooterSettings.imageNames.footerLogo1')
                : currentLogoType === 'paymentLogos'
                  ? t('ecommerceFooterSettings.imageNames.paymentLogos')
                  : t('ecommerceFooterSettings.imageNames.footerLogo2')
        }
        currentImageUrls={
          currentLogoType === 'newsletter'
            ? (footerSettings.newsletterImage ? [footerSettings.newsletterImage] : [])
            : currentLogoType === 'newsletterMobile'
              ? (footerSettings.newsletterMobileImage ? [footerSettings.newsletterMobileImage] : [])
              : currentLogoType === 'logo1'
                ? (footerSettings.companyLogo ? [footerSettings.companyLogo] : [])
                : currentLogoType === 'paymentLogos'
                  ? footerSettings.paymentLogosImages
                  : (footerSettings.companyLogo2 ? [footerSettings.companyLogo2] : [])
        }
        maxSelection={currentLogoType === 'paymentLogos' ? 8 : undefined}
        productId={null}
      />
    </Paper>
  );
};

// SocialMediaModal Component (within the same file)
interface SocialMediaModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: SocialLink) => void;
  existingLink: SocialLink | null;
}

const SOCIAL_URL_PLACEHOLDERS: Record<SocialPlatform, string> = {
  instagram: 'https://instagram.com/username',
  facebook: 'https://facebook.com/page',
  twitter: 'https://x.com/username',
  linkedin: 'https://linkedin.com/company/name',
  youtube: 'https://youtube.com/@channel',
  pinterest: 'https://pinterest.com/username',
  appstore: 'https://apps.apple.com/app/id...',
  googleplay: 'https://play.google.com/store/apps/details?id=...',
  appgallery: 'https://appgallery.huawei.com/app/...',
};

const SocialMediaModal: React.FC<SocialMediaModalProps> = ({ open, onClose, onSave, existingLink }) => {
  const { t } = useTranslation('salesSystem');
  const [platform, setPlatform] = useState<SocialPlatform>('instagram');
  const [url, setUrl] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('icon1');

  useEffect(() => {
    if (existingLink) {
      setPlatform(existingLink.platform);
      setUrl(existingLink.url);
      setSelectedIcon(existingLink.icon);
    } else {
      setPlatform('instagram');
      setUrl('');
      setSelectedIcon('icon1');
    }
  }, [existingLink, open]);

  const handleSubmit = () => {
    if (!url.trim()) return;
    onSave({
      id: existingLink?.id || new Date().toISOString(),
      platform,
      icon: selectedIcon,
      url,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existingLink ? t('ecommerceFooterSettings.modals.editSocialMediaLink') : t('ecommerceFooterSettings.modals.addSocialMediaLink')}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('ecommerceFooterSettings.socialMedia.platform')}</InputLabel>
          <Select
            value={platform}
            label={t('ecommerceFooterSettings.socialMedia.platform')}
            onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
          >
            <ListSubheader>{t('ecommerceFooterSettings.socialMedia.groupSocial')}</ListSubheader>
            {SOCIAL_PLATFORM_OPTIONS.filter((p) => !['appstore', 'googleplay', 'appgallery'].includes(p.value)).map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
            <ListSubheader>{t('ecommerceFooterSettings.socialMedia.groupApps')}</ListSubheader>
            {SOCIAL_PLATFORM_OPTIONS.filter((p) => ['appstore', 'googleplay', 'appgallery'].includes(p.value)).map((p) => (
              <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Box my={2}>
          <Typography variant="subtitle2" gutterBottom>{t('ecommerceFooterSettings.socialMedia.iconSelection')}</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {Array.from({ length: 6 }).map((_, index) => (
              <IconButton 
                key={index} 
                onClick={() => setSelectedIcon(`icon${index + 1}`)}
                sx={{ border: selectedIcon === `icon${index + 1}` ? '2px solid' : '1px solid', borderColor: selectedIcon === `icon${index + 1}` ? 'primary.main' : 'divider', p: 1 }}
              >
                <PanelSocialIcon platform={platform} icon={`icon${index + 1}`} size={28} />
              </IconButton>
            ))}
          </Box>
        </Box>

        <Box
          component="input"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={SOCIAL_URL_PLACEHOLDERS[platform]}
          sx={{ 
            width: '100%',
            p: 2, 
            border: '1px solid #ccc', 
            borderRadius: 1,
            fontFamily: 'inherit',
            fontSize: '16px',
            mt: 2
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!url.trim()}>
          {existingLink ? t('ecommerceFooterSettings.modals.update') : t('ecommerceFooterSettings.modals.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// QuickAccessLinkModal Component
interface QuickAccessLinkModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (link: QuickLink) => void;
  existingLink: QuickLink | null;
  columns: QuickAccessColumn[];
  defaultColumn?: number;
  getColumnTitle: (column: QuickAccessColumn, index: number) => string;
  configId: string;
}

const QuickAccessLinkModal: React.FC<QuickAccessLinkModalProps> = ({
  open,
  onClose,
  onSave,
  existingLink,
  columns,
  defaultColumn,
  getColumnTitle,
  configId,
}) => {
  const { t } = useTranslation('salesSystem');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [column, setColumn] = useState<number>(columns[0]?.id ?? 1);
  const [linkType, setLinkType] = useState<'url' | 'page'>('page');
  const [pageKey, setPageKey] = useState('about');
  const [locationValue, setLocationValue] = useState('top');
  const [pageOptions, setPageOptions] = useState<FooterPageOption[]>(FOOTER_PAGE_OPTIONS);
  const [tabLocationsByPage, setTabLocationsByPage] = useState<Record<string, FooterLocationOption[]>>({});
  const [loadingDestinations, setLoadingDestinations] = useState(false);

  useEffect(() => {
    if (existingLink) {
      setLabel(existingLink.label);
      setUrl(existingLink.url);
      setColumn(existingLink.column);
      setLinkType(existingLink.linkType === 'url' ? 'url' : existingLink.linkType === 'page' ? 'page' : 'url');
      setPageKey(existingLink.pageKey || 'about');
      setLocationValue(resolveLocationValue(existingLink));
    } else {
      setLabel('');
      setUrl('');
      setColumn(defaultColumn ?? columns[0]?.id ?? 1);
      setLinkType('page');
      setPageKey('about');
      setLocationValue('faq');
    }
  }, [existingLink, open, defaultColumn, columns]);

  useEffect(() => {
    if (!open || !configId) return;
    let cancelled = false;
    setLoadingDestinations(true);

    const loadDestinations = async () => {
      try {
        const [tabResponses, customPagesResponse] = await Promise.all([
          Promise.all(
            TAB_TARGET_CONFIGS.map((cfg) =>
              getAboutTabs(configId, cfg.pageType)
                .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
                .catch(() => [])
            )
          ),
          getCustomPages(configId)
            .then((res: any) => (Array.isArray(res?.data) ? res.data : []))
            .catch(() => []),
        ]);

        if (cancelled) return;

        const nextTabLocations: Record<string, FooterLocationOption[]> = {};
        TAB_TARGET_CONFIGS.forEach((cfg, index) => {
          nextTabLocations[cfg.pageKey] = buildTabLocations(cfg.pageKey, tabResponses[index]);
        });
        setTabLocationsByPage(nextTabLocations);

        const customPages = (customPagesResponse as any[])
          .filter((page) => page?.isActive !== false && page?.IsActive !== false && (page?.slug || page?.id))
          .map((page) => ({
            value: `custom:${String(page.slug || page.id)}`,
            label: String(page.title || page.slug || 'Özel Sayfa'),
            group: 'Özel sayfalar',
          }));
        setPageOptions([...FOOTER_PAGE_OPTIONS, ...customPages]);
      } finally {
        if (!cancelled) setLoadingDestinations(false);
      }
    };

    void loadDestinations();
    return () => {
      cancelled = true;
    };
  }, [open, configId]);

  const locationOptions = [
    ...getStaticLocationsForPage(pageKey),
    ...(tabLocationsByPage[pageKey] || []),
  ];

  const selectedLocation = locationOptions.find((option) => option.value === locationValue);

  const resolvedLocation: FooterLocationOption = selectedLocation || {
    value: locationValue || 'top',
    label: existingLink?.locationLabel || t('ecommerceFooterSettings.modals.pageTop'),
    group: 'Sayfa bölümleri',
    scrollTarget: existingLink?.scrollTarget,
    tabId: existingLink?.tabId,
    sectionId: existingLink?.sectionId,
    subTabId: existingLink?.subTabId,
  };

  const pageLabel = pageOptions.find((option) => option.value === pageKey)?.label || pageKey;
  const locationLabel =
    resolvedLocation.value === 'top'
      ? pageLabel
      : `${pageLabel} → ${selectedLocation?.label || existingLink?.locationLabel || resolvedLocation.label}`;

  const canSubmit =
    Boolean(label.trim()) &&
    (linkType === 'url' ? Boolean(url.trim()) : Boolean(pageKey));

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (linkType === 'url') {
      onSave({
        id: existingLink?.id || new Date().toISOString(),
        label: label.trim(),
        url: url.trim(),
        column,
        linkType: 'url',
        pageKey: undefined,
        scrollTarget: undefined,
        tabId: undefined,
        sectionId: undefined,
        subTabId: undefined,
        locationLabel: undefined,
      });
      return;
    }

    const computedUrl = buildPageLinkUrl({
      pageKey,
      scrollTarget: resolvedLocation.scrollTarget,
      tabId: resolvedLocation.tabId,
      sectionId: resolvedLocation.sectionId,
      subTabId: resolvedLocation.subTabId,
    });

    onSave({
      id: existingLink?.id || new Date().toISOString(),
      label: label.trim(),
      url: computedUrl,
      column,
      linkType: 'page',
      pageKey,
      scrollTarget: resolvedLocation.scrollTarget,
      tabId: resolvedLocation.tabId,
      sectionId: resolvedLocation.sectionId,
      subTabId: resolvedLocation.subTabId,
      locationLabel,
    });
  };

  const locationGroups = locationOptions.reduce<string[]>((groups, option) => {
    if (!groups.includes(option.group)) groups.push(option.group);
    return groups;
  }, []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{existingLink ? t('ecommerceFooterSettings.modals.editQuickAccessLink') : t('ecommerceFooterSettings.modals.addQuickAccessLink')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('ecommerceFooterSettings.modals.displayName')}
          type="text"
          fullWidth
          variant="outlined"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">{t('ecommerceFooterSettings.modals.linkType')}</FormLabel>
          <RadioGroup
            row
            value={linkType}
            onChange={(e) => setLinkType(e.target.value as 'url' | 'page')}
          >
            <FormControlLabel
              value="page"
              control={<Radio />}
              label={t('ecommerceFooterSettings.modals.pageLocation')}
            />
            <FormControlLabel
              value="url"
              control={<Radio />}
              label={t('ecommerceFooterSettings.modals.customUrl')}
            />
          </RadioGroup>
        </FormControl>
        {linkType === 'url' ? (
          <TextField
            margin="dense"
            label={t('ecommerceFooterSettings.modals.urlLink')}
            type="url"
            fullWidth
            variant="outlined"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('ecommerceFooterSettings.modals.targetPage')}</InputLabel>
              <Select
                value={pageKey}
                label={t('ecommerceFooterSettings.modals.targetPage')}
                onChange={(e) => {
                  setPageKey(String(e.target.value));
                  setLocationValue(String(e.target.value) === 'about' ? 'faq' : 'top');
                }}
              >
                {pageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('ecommerceFooterSettings.modals.targetLocation')}</InputLabel>
              <Select
                value={locationOptions.some((option) => option.value === locationValue) ? locationValue : 'top'}
                label={t('ecommerceFooterSettings.modals.targetLocation')}
                onChange={(e) => setLocationValue(String(e.target.value))}
              >
                {locationGroups.flatMap((group) => [
                  <ListSubheader key={`group-${group}`}>{group}</ListSubheader>,
                  ...locationOptions
                    .filter((option) => option.group === group)
                    .map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    )),
                ])}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {loadingDestinations
                ? t('ecommerceFooterSettings.modals.loadingLocations')
                : t('ecommerceFooterSettings.modals.pageLocationHint')}
              {loadingDestinations && (
                <CircularProgress size={12} sx={{ ml: 1, verticalAlign: 'middle' }} />
              )}
            </Typography>
          </>
        )}
        <FormControl fullWidth>
          <InputLabel>{t('ecommerceFooterSettings.modals.column')}</InputLabel>
          <Select
            value={column}
            label={t('ecommerceFooterSettings.modals.column')}
            onChange={(e) => setColumn(Number(e.target.value))}
          >
            {columns.map((col, index) => (
              <MenuItem key={col.id} value={col.id}>
                {getColumnTitle(col, index)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('ecommerceFooterSettings.modals.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {existingLink ? t('ecommerceFooterSettings.modals.update') : t('ecommerceFooterSettings.modals.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FooterSettings; 